<template>
  <!-- R6: no celular (`mobileShell`, só no dashboard) o MESMO conteúdo vai para o
       FullScreenPanel; no desktop é o ModalVue de sempre (os atributos abaixo
       passam intactos para ele). -->
  <DashboardModalShell
    :open="showModal"
    :full-screen="isFullScreen"
    title="Detalhes do Plantão"
    :meta="mobileMeta"
    test-id="station-details-panel"
    @close="close"
    :hasHeaderBorder="true"
    :hasFooterBorder="true"
    :class="{
      'modal-pushed-by-sidebar':
        isPushedExternally ||
        isOpenExceptionalitySidebar ||
        isOpenRequestExceptionality ||
        isOpenHistorySidebar ||
        isPostStationSidebarOpen
    }"
    :min-height="'40vh'"
  >
    <template #header>
      <template v-if="loading">
        <StationDetailsHeaderSkeleton />
      </template>

      <div
        v-else-if="stationDetails"
        class="text-left flex flex-col text-textPrimary relative w-full"
      >
        <div class="flex items-center gap-2 md:gap-3 mb-1 flex-wrap">
          <StationIdBadge
            :stationId="stationDetails?.id"
            :doctorStationId="stationDetails?.doctorStation?.id"
          />

          <span class="font-bold text-lg md:text-2xl text-textPrimary">Detalhes do Plantão</span>
        </div>

        <span class="text-xs md:text-base truncate">
          {{ stationDetails?.schedule?.healthUnit?.company?.name || 'Empresa não informada' }} >
          {{ stationDetails?.schedule?.healthUnit?.name || 'Unidade não informada' }} >
          {{ stationDetails?.schedule?.name || 'Escala não informada' }} >
          {{ stationDetails?.position?.name || 'Posição não definida' }}
        </span>
      </div>

      <!-- Desktop: absolute positioned action buttons -->
      <!--
        `z-20` no CONTAINER, não nos botões: sem ele o cluster fica em
        `z-index: auto` e o `CardDutyWithDoctor` (também `relative` e `auto`,
        porém depois na árvore e com fundo branco) pinta por cima pela ordem de
        pintura do CSS. Empilhar aqui cria um stacking context e sobe a coluna
        inteira de uma vez — o remendo `z-[1]` botão a botão só funcionava
        enquanto cada botão fosse flex item direto.

        `border-solid` explícito nos botões outline não é redundante: o reset do
        Vuetify (`button { border-style: none }`, importado DEPOIS do
        `tailwind.css` em `app.ts`) derruba o `border-style: solid` que o
        preflight do Tailwind dá a `*`. Sem ele, `border-2` define só a largura
        e a borda não aparece em `<button>` — só em `<div>`, como o toggle "⋯".
      -->
      <div
        v-if="loading || (stationDetails && mode === 'dashboard' && flags.enabledSmartAllocation)"
        class="hidden md:flex absolute right-[2.5vw] top-[11vh] z-20 flex-col items-end gap-3"
      >
        <template v-if="loading"> </template>
        <template v-else>
          <span v-if="actionMap.allocate" class="relative inline-flex">
            <button
              data-testid="station-details-allocate-doctor-button"
              :disabled="actionMap.allocate.disabled"
              :title="isUnidentifiedStation ? 'Identificar médico' : undefined"
              :aria-label="isUnidentifiedStation ? 'Identificar médico' : undefined"
              @click="actionMap.allocate.handler()"
              class="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-md hover:bg-primary40 focus:outline-none focus:ring-2 focus:ring-primary30 focus:ring-opacity-75 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
            >
              <AccountSwitchIcon
                v-if="stationDetails?.doctorStation.doctor"
                color="white"
                class="w-[18px] h-[18px]"
              />
              <AccountPlus v-else color="white" class="w-[18px] h-[18px]" />
            </button>
            <!-- Button nativo disabled não dispara hover: o tooltip escuta o
                 wrapper (activator="parent"), não o próprio button. -->
            <v-tooltip
              v-if="actionMap.allocate.disabled"
              activator="parent"
              location="top"
              :text="actionMap.allocate.disabledReason"
            />
          </span>

          <!-- Q35: "Enviar registro" vem em SEGUNDO no menu flutuante da vaga
               (era PRIMEIRO na Q23) — FORA do Editar e FORA dos três
               pontinhos. Só habilita depois que o plantão termina (o médico
               ainda pode estar trabalhando); antes disso o tooltip nativo diz
               isso, porque é o único jeito de mostrar o motivo COM o botão
               desabilitado sem um wrapper extra. -->
          <button
            v-if="actionMap['send-record']"
            type="button"
            data-testid="station-details-send-record"
            :disabled="actionMap['send-record'].disabled"
            :title="sendRecordTooltip"
            class="w-12 h-12 rounded-full border-2 border-solid border-primary30 bg-white flex items-center justify-center shadow-md hover:bg-primary5 focus:outline-none focus:ring-2 focus:ring-primary30 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
            @click="actionMap['send-record'].handler()"
          >
            <SVGIcon
              :path="mdiUpload"
              aria-hidden="true"
              class="w-[18px] h-[18px] text-primary30"
            />
          </button>

          <PadlockButton
            v-if="actionMap.exceptionality"
            :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
            :is-active="isOpenExceptionalitySidebar"
            @click="actionMap.exceptionality.handler()"
            :loading="loading"
          />

          <!-- Fora do speed dial de propósito: as tentativas de check são o
               primeiro lugar onde se olha quando um médico reclama que "o app
               não deixou bater o ponto", então o botão fica sempre visível. -->
          <CheckAttemptsButton
            v-if="actionMap['check-attempts']"
            test-id="station-details-check-attempts-toggle"
            :is-active="showCheckAttempts"
            @click="actionMap['check-attempts'].handler()"
          />

          <div v-if="isOverflowAvailable" class="relative flex flex-col items-center">
            <div
              data-testid="station-details-speed-dial-toggle"
              @click="toggleSpeedDial"
              class="w-12 h-12 rounded-full border-2 border-solid border-primary30 flex items-center justify-center shadow-md bg-white cursor-pointer"
            >
              <DotsHorizontal
                color="#2F7F91"
                class="w-[18px] h-[18px] transition-transform duration-200"
                :class="{ 'rotate-90': isSpeedDialOpen }"
              />
            </div>
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="transform opacity-0 scale-95 -translate-y-2"
              enter-to-class="transform opacity-100 scale-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="transform opacity-100 scale-100 translate-y-0"
              leave-to-class="transform opacity-0 scale-95 -translate-y-2"
            >
              <div
                v-if="isSpeedDialOpen"
                class="absolute top-full mt-3 flex flex-col items-center gap-3 z-10"
              >
                <!-- Não trava: abre o `update.station.modal`, que já trava
                     internamente (Pós-Plantão e perna travada: banner, campos
                     inertes, Excluir desabilitado). -->
                <span v-if="actionMap.edit" class="relative inline-flex">
                  <button
                    type="button"
                    data-testid="station-details-edit-station-overflow-button"
                    :disabled="actionMap.edit.disabled"
                    @click="actionMap.edit.handler()"
                    class="w-9 h-9 rounded-full border-2 border-solid border-primary30 bg-white flex items-center justify-center shadow-md hover:bg-primary5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                    title="Editar Plantão"
                  >
                    <EditPencilIcon color="#2F7F91" class="w-[18px] h-[18px]" />
                  </button>
                  <v-tooltip
                    v-if="actionMap.edit.disabled"
                    activator="parent"
                    location="top"
                    :text="actionMap.edit.disabledReason"
                  />
                </span>

                <span v-if="actionMap.remove" class="relative inline-flex">
                  <button
                    type="button"
                    data-testid="station-details-remove-doctor-button"
                    :disabled="actionMap.remove.disabled"
                    class="w-9 h-9 rounded-full border-2 border-solid border-negative bg-white flex items-center justify-center shadow-md hover:bg-negative/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                    title="Remover Médico"
                    @click="actionMap.remove.handler()"
                  >
                    <AccountRemove color="red" class="w-[18px] h-[18px]" />
                  </button>
                  <!-- Button nativo disabled não dispara hover: o tooltip escuta
                       o wrapper (activator="parent"), não o próprio button. -->
                  <v-tooltip
                    v-if="actionMap.remove.disabled"
                    activator="parent"
                    location="top"
                    :text="actionMap.remove.disabledReason"
                  />
                </span>

                <HistoryButton
                  v-if="actionMap.history"
                  :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
                  :is-active="isOpenHistorySidebar"
                  @click="actionMap.history.handler()"
                  size="small"
                  :loading="loading"
                />
              </div>
            </transition>
          </div>
        </template>
      </div>

      <!-- Mobile: horizontal action buttons bar (smart allocation) -->
      <div
        v-if="!loading && stationDetails && mode === 'dashboard' && flags.enabledSmartAllocation"
        class="flex md:hidden flex-row items-center gap-2 mt-2"
      >
        <span v-if="actionMap.allocate" class="relative inline-flex">
          <button
            data-testid="station-details-allocate-doctor-button-mobile"
            :disabled="actionMap.allocate.disabled"
            :title="isUnidentifiedStation ? 'Identificar médico' : undefined"
            :aria-label="isUnidentifiedStation ? 'Identificar médico' : undefined"
            @click="actionMap.allocate.handler()"
            class="w-9 h-9 rounded-full bg-primary flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AccountSwitchIcon
              v-if="stationDetails?.doctorStation.doctor"
              color="white"
              class="w-[14px] h-[14px]"
            />
            <AccountPlus v-else color="white" class="w-[14px] h-[14px]" />
          </button>
          <v-tooltip
            v-if="actionMap.allocate.disabled"
            activator="parent"
            location="top"
            :text="actionMap.allocate.disabledReason"
          />
        </span>

        <!-- Q35: "Enviar registro" em SEGUNDO (era primeiro na Q23). -->
        <button
          v-if="actionMap['send-record']"
          type="button"
          data-testid="station-details-send-record-mobile"
          :disabled="actionMap['send-record'].disabled"
          :title="sendRecordTooltip"
          class="w-9 h-9 rounded-full border-2 border-solid border-primary30 bg-white flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          @click="actionMap['send-record'].handler()"
        >
          <SVGIcon :path="mdiUpload" class="w-[14px] h-[14px] text-primary30" />
        </button>

        <PadlockButton
          v-if="actionMap.exceptionality"
          :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
          :is-active="isOpenExceptionalitySidebar"
          @click="actionMap.exceptionality.handler()"
          :loading="loading"
          size="small"
        />

        <CheckAttemptsButton
          v-if="actionMap['check-attempts']"
          test-id="station-details-check-attempts-toggle-mobile"
          :is-active="showCheckAttempts"
          size="small"
          @click="actionMap['check-attempts'].handler()"
        />

        <!-- Não trava: abre o `update.station.modal`, que já trava internamente. -->
        <span v-if="actionMap.edit" class="relative inline-flex">
          <button
            type="button"
            data-testid="station-details-edit-station-overflow-button-mobile"
            :disabled="actionMap.edit.disabled"
            @click="actionMap.edit.handler()"
            class="w-9 h-9 rounded-full border-2 border-solid border-primary30 bg-white flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            title="Editar Plantão"
          >
            <EditPencilIcon color="#2F7F91" class="w-[14px] h-[14px]" />
          </button>
          <v-tooltip
            v-if="actionMap.edit.disabled"
            activator="parent"
            location="top"
            :text="actionMap.edit.disabledReason"
          />
        </span>

        <span v-if="actionMap.remove" class="relative inline-flex">
          <button
            type="button"
            data-testid="station-details-remove-doctor-button-mobile"
            :disabled="actionMap.remove.disabled"
            class="w-9 h-9 rounded-full border-2 border-solid border-negative bg-white flex items-center justify-center shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title="Remover Médico"
            @click="actionMap.remove.handler()"
          >
            <AccountRemove color="red" class="w-[14px] h-[14px]" />
          </button>
          <v-tooltip
            v-if="actionMap.remove.disabled"
            activator="parent"
            location="top"
            :text="actionMap.remove.disabledReason"
          />
        </span>

        <HistoryButton
          v-if="actionMap.history"
          :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
          :is-active="isOpenHistorySidebar"
          @click="actionMap.history.handler()"
          size="small"
          :loading="loading"
        />
      </div>

      <!-- Desktop: absolute positioned action buttons (no smart allocation) -->
      <div
        v-if="!loading && stationDetails && mode === 'dashboard' && !flags.enabledSmartAllocation"
        class="hidden md:flex absolute right-[2.5vw] top-[11vh] z-20 flex-col items-end gap-3"
      >
        <span v-if="actionMap.edit" class="relative inline-flex">
          <button
            data-testid="station-details-edit-station-button"
            :disabled="actionMap.edit.disabled"
            @click="actionMap.edit.handler()"
            class="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-md hover:bg-primary40 focus:outline-none focus:ring-2 focus:ring-primary30 focus:ring-opacity-75 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
          >
            <EditPencilIcon color="white" class="w-[18px] h-[18px]" />
          </button>
          <v-tooltip
            v-if="actionMap.edit.disabled"
            activator="parent"
            location="top"
            :text="actionMap.edit.disabledReason"
          />
        </span>

        <!-- Q35: "Enviar registro" em SEGUNDO. Mesma ação primária da variante
             com smart allocation: o menu flutuante desta tela também abre o
             hub "Enviar registro". -->
        <button
          v-if="actionMap['send-record']"
          type="button"
          data-testid="station-details-send-record-legacy"
          :disabled="actionMap['send-record'].disabled"
          :title="sendRecordTooltip"
          class="w-12 h-12 rounded-full border-2 border-solid border-primary30 bg-white flex items-center justify-center shadow-md hover:bg-primary5 focus:outline-none focus:ring-2 focus:ring-primary30 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          @click="actionMap['send-record'].handler()"
        >
          <SVGIcon :path="mdiUpload" class="w-[18px] h-[18px] text-primary30" />
        </button>

        <PadlockButton
          v-if="actionMap.exceptionality"
          :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
          :is-active="isOpenExceptionalitySidebar"
          @click="actionMap.exceptionality.handler()"
          :loading="loading"
        />
        <CheckAttemptsButton
          v-if="actionMap['check-attempts']"
          test-id="station-details-check-attempts-toggle"
          :is-active="showCheckAttempts"
          @click="actionMap['check-attempts'].handler()"
        />
        <HistoryButton
          v-if="actionMap.history"
          :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
          :is-active="isOpenHistorySidebar"
          @click="actionMap.history.handler()"
          :loading="loading"
        />
      </div>

      <!-- Mobile: horizontal action buttons bar (no smart allocation) -->
      <div
        v-if="!loading && stationDetails && mode === 'dashboard' && !flags.enabledSmartAllocation"
        class="flex md:hidden flex-row items-center gap-2 mt-2"
      >
        <span v-if="actionMap.edit" class="relative inline-flex">
          <button
            data-testid="station-details-edit-station-button-mobile"
            :disabled="actionMap.edit.disabled"
            @click="actionMap.edit.handler()"
            class="w-9 h-9 rounded-full bg-primary flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <EditPencilIcon color="white" class="w-[14px] h-[14px]" />
          </button>
          <v-tooltip
            v-if="actionMap.edit.disabled"
            activator="parent"
            location="top"
            :text="actionMap.edit.disabledReason"
          />
        </span>

        <!-- Q35: "Enviar registro" em SEGUNDO. -->
        <button
          v-if="actionMap['send-record']"
          type="button"
          data-testid="station-details-send-record-legacy-mobile"
          :disabled="actionMap['send-record'].disabled"
          :title="sendRecordTooltip"
          class="w-9 h-9 rounded-full border-2 border-solid border-primary30 bg-white flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          @click="actionMap['send-record'].handler()"
        >
          <SVGIcon :path="mdiUpload" class="w-[14px] h-[14px] text-primary30" />
        </button>

        <PadlockButton
          v-if="actionMap.exceptionality"
          :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
          :is-active="isOpenExceptionalitySidebar"
          @click="actionMap.exceptionality.handler()"
          :loading="loading"
          size="small"
        />
        <CheckAttemptsButton
          v-if="actionMap['check-attempts']"
          test-id="station-details-check-attempts-toggle-mobile"
          :is-active="showCheckAttempts"
          size="small"
          @click="actionMap['check-attempts'].handler()"
        />
        <HistoryButton
          v-if="actionMap.history"
          :status="stationDetails?.doctorStation?.exceptionSummaryStatus || null"
          :is-active="isOpenHistorySidebar"
          @click="actionMap.history.handler()"
          :loading="loading"
          size="small"
        />
      </div>
    </template>

    <!-- Celular: selos abaixo da meta (tipo, setor, especialidade, Externo). -->
    <template v-if="mobileBadges.length" #header-extra>
      <div class="flex flex-wrap gap-1.5" data-testid="station-details-mobile-badges">
        <span
          v-for="badge in mobileBadges"
          :key="badge.id"
          :data-testid="`station-details-mobile-badge-${badge.id}`"
          class="rounded px-2 py-0.5 text-xs font-medium text-textPrimary"
          :class="badge.class"
        >
          {{ badge.label }}
        </span>
      </div>
    </template>

    <!-- Celular: dock fixo com as ações do menu flutuante (mesmas condições). Some
         enquanto o detalhe (re)carrega ou falha: não age sobre o plantão anterior. -->
    <template v-if="isDetailReady && stationActions.length" #dock>
      <StationDetailsDock :actions="stationActions" />
    </template>

    <div class="flex flex-col w-full" :class="isFullScreen ? '' : 'max-h-[70vh]'">
      <template v-if="loading">
        <StationDetailsSkeleton />
      </template>

      <div v-else-if="errorMessage" class="text-center text-red-500 py-4">{{ errorMessage }}</div>

      <div v-else-if="stationDetails">
        <div class="flex flex-col gap-4 pb-4">
          <div
            id="allocated-by-info"
            v-if="!isUnidentifiedStation && stationDetails?.doctorStation?.userSponsor?.name"
            class="flex gap-2"
          >
            <span class="font-bold text-gray-500">Escalado por:</span
            ><span>{{ stationDetails.doctorStation.userSponsor.name }}</span>
            <span
              v-if="stationDetails.doctorStation.userSponsor.registration"
              class="bg-gray-200 rounded px-2"
            >
              #{{ stationDetails.doctorStation.userSponsor.registration }}
            </span>
          </div>
          <div id="shift-period-timezone" class="text-left flex flex-col">
            <span v-if="formattedStartDate">
              De
              <span class="font-bold">{{ formattedStartDate.weekday }},</span>
              {{ formattedStartDate.date }}
              <span class="font-bold">às {{ formattedStartDate.time }}</span>
            </span>

            <span class="text-left" v-if="formattedEndDate">
              Até
              <span class="font-bold">{{ formattedEndDate.weekday }},</span>
              {{ formattedEndDate.date }}
              <span class="font-bold">às {{ formattedEndDate.time }}</span>
            </span>

            <span class="text-sm" v-if="getTimezoneTitle(stationDetails.timezone)">
              Horário de {{ getTimezoneTitle(stationDetails.timezone) }}
            </span>
          </div>

          <OptionalCollapsible
            v-if="isUnidentifiedStation"
            :enabled="isFullScreen"
            title="Médico"
            test-id="mobile-doctor-accordion"
            :summary="mobileSummaries.doctor"
          >
            <UnidentifiedCoverageDetails
              :responsible-name="coverageInfo.responsibleName"
              :note="coverageInfo.note"
              :station-type-label="unidentifiedStationTypeLabel"
              :position-name="stationDetails.position?.name"
              :shift-name="stationDetails.shift?.name"
              :reference-value="unidentifiedReferenceValue"
              :show-actions="showCoverageActions"
              :can-identify="canAllocateDoctor"
              :can-edit-responsible="canEditUnidentifiedResponsible"
              :can-release="canRemoveDoctor"
              :actions-disabled="isPostStationLocked"
              :reserve-actions-lane="mode === 'dashboard' && !isFullScreen"
              @identify="openManagement(CoverageMode.DOCTOR)"
              @edit-responsible="openManagement(CoverageMode.UNIDENTIFIED)"
              @release="openRelease"
            />
          </OptionalCollapsible>

          <OptionalCollapsible
            v-else-if="!stationDetails.doctorStation?.doctor"
            :enabled="isFullScreen"
            title="Médico"
            test-id="mobile-doctor-accordion"
            :summary="mobileSummaries.doctor"
          >
            <div>
              <div class="flex mb-3">
                <span class="text-left text-base rounded py-1 px-2" :class="stationTypeClass">
                  {{ formattedStationType }}</span
                >
              </div>

              <div
                v-if="formattedStationType !== 'ABERTA' && canReadPaymentRules"
                class="text-left gap-4 flex flex-col"
              >
                <span class="font-bold">Valores do plantão</span>
                <div class="flex flex-row flex-wrap gap-2 mb-3">
                  <CardDutyFee
                    v-for="(card, index) in cards"
                    :key="index"
                    :formation="card.formation"
                    :value="formattedStationType === 'ABERTA' ? undefined : card.value"
                    :repassMode="card.repassMode"
                    :active="stationDetails.value === card.value"
                  />
                </div>
              </div>

              <div
                v-if="canReadScheduleRequirements"
                class="grid grid-cols-2 md:grid-cols-4 gap-3 text-left"
              >
                <div v-if="formattedStationType !== 'ABERTA'" class="flex flex-col">
                  <span class="font-bold">Time</span>
                  <span v-if="formattedStationType !== 'ABERTA'">{{
                    stationDetails.ourTeam ? 'Interno' : 'Externo'
                  }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="font-bold">Posição</span>
                  <span>{{ stationDetails.position?.name || 'Não definida' }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="font-bold">Turno</span>
                  <span>{{ stationDetails.shift?.name || 'Não definido' }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="font-bold">Regra de proporcionalidade</span>
                  <span>{{ proportionalityRuleTitle }}</span>
                </div>
              </div>
            </div>
          </OptionalCollapsible>

          <div v-else id="duty-details-section" class="flex flex-col gap-4">
            <!--
              Reserva da faixa dos botões de ação, só nesta linha. Eles são
              `absolute right-[2.5vw] top-[11vh]` ancorados no `.modal-content`:
              moram no `<header>` e não rolam com o conteúdo, então a faixa fica
              parada bem na altura desta linha — é aqui que o "Valor" ficava por
              baixo do botão de opções.

              A conta: `2.5vw` (o afastamento dos botões) + 48px (`w-12`), menos
              os 24px que o `md:px-6` do `<main>` já dá, mais ~8px de folga. O
              `2.5vw` acompanha o viewport porque o dos botões também acompanha;
              um valor fixo deixaria de cobrir a faixa nas telas largas.

              Só nesta linha, e não no root do conteúdo: ali protegeria também as
              seções que rolam por baixo da faixa, mas ao custo de nenhuma seção
              alcançar a borda direita — nem as que nunca chegam perto dos
              botões. O recorte é o mesmo que o #2348 já fazia, agora sem
              deformar o painel de Valor.
            -->
            <div
              class="flex flex-col md:flex-row gap-4 md:gap-6"
              :class="mode === 'dashboard' && !isFullScreen ? 'md:pr-[calc(2.5vw_+_32px)]' : ''"
            >
              <OptionalCollapsible
                :enabled="isFullScreen"
                title="Médico"
                test-id="mobile-doctor-accordion"
                :summary="mobileSummaries.doctor"
              >
                <CardDutyWithDoctor
                  :formation="stationDetails?.doctorStation?.dsPaymentDoctorFormation?.name || ''"
                  :team="stationDetails?.doctorStation?.ourTeam ? 'Interno' : 'Externo'"
                  :position="stationDetails?.position?.name"
                  :shift="stationDetails?.shift?.name || ''"
                  :dutyLabel="stationDetails?.doctorStation?.type"
                  :doctorName="stationDetails?.doctorStation?.doctor?.mainCrm?.name"
                  :doctorCrm="doctorCrmLabel"
                  :active="false"
                  :scheduleId="stationDetails.schedule.id"
                  :npsScore="stationDetails?.doctorNpsScore?.npsScore"
                  :avatarUrl="stationDetails?.doctorStation?.doctor?.user?.avatarUrl"
                >
                </CardDutyWithDoctor>
              </OptionalCollapsible>

              <!-- Sem folga própria: a faixa dos botões já é reservada pela
                   linha que envolve este painel (ver comentário acima), então
                   ele não precisa mais se deformar para fugir dela. O remédio
                   antigo (`md:justify-end` + `md:min-h-[176px]`, #2348) empurrava
                   o valor para a base do painel e forçava 176px de altura
                   mínima — funcionava, mas distorcia o painel e só protegia
                   este bloco. -->
              <OptionalCollapsible
                :enabled="isFullScreen && hasReadableValues"
                title="Valores do plantão"
                test-id="mobile-values-accordion"
                :summary="mobileSummaries.values"
                :default-expanded="false"
              >
                <ValueDetailsSection
                  :original-value="valuePanelOriginalValue"
                  :approved-value="
                    mode === 'approvals' &&
                    exceptionSummary?.financials?.totalExceptionalValue != null
                      ? Number(exceptionSummary.financials.totalExceptionalValue)
                      : null
                  "
                  :request-antecipe="stationDetails.doctorStation?.requestAntecipe"
                  :antecipe-was-paid="stationDetails.doctorStation?.antecipeWasPaid"
                  :is-exception-mode="mode === 'approvals' && !!exceptionSummary"
                  :approval-status="props.approvalStatusMap?.financials"
                  :scheduleId="stationDetails.schedule.id"
                  :show-exception-lock="mode === 'dashboard' && !!exceptionSummary"
                />
              </OptionalCollapsible>
            </div>

            <div v-if="exceptionSummary?.changes && mode === 'approvals'">
              <RepassProportionalityDetailsSection
                :original-repass-mode="exceptionSummary?.changes.repassMode?.original"
                :current-repass-mode="exceptionSummary?.changes.repassMode?.requested"
                :original-proportionality-rule="
                  exceptionSummary?.changes.proportionalityRule?.original
                "
                :current-proportionality-rule="
                  exceptionSummary?.changes.proportionalityRule?.requested
                "
                :repass-mode-status="props.approvalStatusMap?.repassMode"
                :proportionality-rule-status="props.approvalStatusMap?.proportionalityRule"
              />
            </div>
            <!-- Mesmo card das seções vizinhas (Registro de plantão, Local e biometria):
                 sem ele as duas regras flutuavam como texto solto no meio dos cards. -->
            <div
              v-else-if="
                mode === 'dashboard' && (canReadPaymentRules || canReadProportionalityRule)
              "
              class="flex flex-col md:flex-row gap-4"
              data-testid="station-payment-rules-cards"
            >
              <div
                v-if="canReadPaymentRules"
                class="flex flex-1 flex-col gap-1 rounded-lg border border-lightGray30 px-3 py-3 text-left text-sm md:px-4"
                data-testid="station-repass-mode-card"
              >
                <span class="font-bold text-left">
                  <SVGIcon
                    v-if="exceptionSummary"
                    :path="mdiLockOpenVariant"
                    class="inline-block w-4 h-4 text-negative align-text-bottom mr-1"
                  />
                  Modalidade de repasse
                </span>
                <template v-if="stationDetails.doctorStation?.inCash != null">
                  <span class="text-left">
                    {{ stationDetails.doctorStation?.inCash ? 'À vista' : 'Fluxo' }}
                  </span>
                </template>
                <template v-else>
                  <span class="text-left italic text-gray-400">Não definida</span>
                  <span
                    v-if="stationDetails.value == null"
                    class="flex items-center gap-1 text-xs text-left"
                    style="color: #8a5a05"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Nenhuma política de pagamento aplicável
                  </span>
                </template>
              </div>
              <div
                v-if="canReadProportionalityRule"
                class="flex flex-1 flex-col gap-1 rounded-lg border border-lightGray30 px-3 py-3 text-left text-sm md:px-4"
                data-testid="station-proportionality-rule-card"
              >
                <span class="font-bold text-left">
                  <SVGIcon
                    v-if="exceptionSummary"
                    :path="mdiLockOpenVariant"
                    class="inline-block w-4 h-4 text-negative align-text-bottom mr-1"
                  />
                  Regra de proporcionalidade
                </span>
                <template v-if="stationDetails.schedule?.proportionalityRule">
                  <span class="text-left">{{ proportionalityRuleTitle }}</span>
                </template>
                <template v-else>
                  <span class="text-left italic text-gray-400">Não definida</span>
                  <span
                    v-if="stationDetails.value == null"
                    class="flex items-center gap-1 text-xs text-left"
                    style="color: #8a5a05"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Nenhuma política de pagamento aplicável
                  </span>
                </template>
              </div>
            </div>

            <WorkJourneyDetailsSection
              v-if="exceptionSummary?.changes.workJourney && mode === 'approvals'"
              :original-start-hour="exceptionSummary?.changes.workJourney.original.startHour"
              :original-end-hour="exceptionSummary?.changes.workJourney.original.endHour"
              :requested-start-hour="exceptionSummary?.changes.workJourney.requested.startHour"
              :requested-end-hour="exceptionSummary?.changes.workJourney.requested.endHour"
              :timezone="stationDetails.timezone"
              :approval-status="props.approvalStatusMap?.workJourney"
            />

            <!-- M7, Tela 3: quem decidiu a exigência de check deste plantão. Só aparece
                 quando o backend devolve `requiredCheckSource`; sem ele o componente
                 não renderiza nada. -->
            <StationRequiredCheckLine
              v-if="mode === 'dashboard'"
              :required-check-source="stationDetails.requiredCheckSource"
              :required-check-policy="stationDetails.requiredCheckPolicy"
              :required-check="stationDetails.requiredCheck"
              :required-check-method="stationDetails.requiredCheckMethod"
              :our-team="stationDetails.ourTeam"
              :schedule-flags="scheduleCheckFlags"
              :touch-target="isFullScreen"
              @open-policy="openSchedulePolicy"
            />

            <StationRegistrationSection
              v-if="mode === 'dashboard'"
              :touch-target="isFullScreen"
              :exception-summary="exceptionSummary"
              :show-doctor-check="showDoctorCheck"
              :main-check="mainCheck"
              :permanence-visibility="permanenceVisibility"
              :attendance-status="attendanceStatus"
              :loading-ds-evidence="loadingDsEvidence"
              :has-station-started="hasStationStarted"
              :has-station-ended="hasStationEnded"
              :can-read-doctor-check="canReadDoctorCheck"
              :is-conciliation-origin="isConciliationOrigin"
              :checkin="checkin"
              :checkout="checkout"
              :registration="stationRegistration"
              :can-send-record="!isSendRecordDisabled"
              :our-team="stationDetails?.doctorStation?.ourTeam ?? null"
              :load-error="registrationLoadError"
              :expected-checkin="stationDetails.startHour"
              :expected-checkout="stationDetails.endHour"
              :timezone="stationDetails.timezone"
              @navigate-to-station="(stationId) => emit('navigateToStation', stationId)"
              @open-evidence="openEvidence"
              @open-ds-attendance-evidence="openDsAttendanceEvidence"
              @open-verification="openVerification"
              @retry-registration="fetchStationRegistration"
              @send-record="handleSpeedDialAction('send-record')"
              @view-evidence="openStationEvidence"
            />

            <!-- Spec 2026-09-24 §8.3 — todos os registros do plantão e quem vence cada
                 perna. Some quando o plantão não tem registro (ou sem acesso). -->
            <StationRecordsSection
              v-if="mode === 'dashboard' && canReadDoctorCheck && canReadAttendance"
              :touch-target="isFullScreen"
              :doctor-station-id="stationDetails.doctorStation?.id ?? null"
            />

            <!-- Seção nova da F2: os Registros de Atividade desta vaga. Busca
                 sozinha e some quando não há nada a mostrar — o Detalhes do
                 Plantão de quem nunca usou Registro de Atividade fica idêntico. -->
            <StationActivityRecordsSection
              v-if="mode === 'dashboard' && canReadDoctorCheck"
              :touch-target="isFullScreen"
              ref="activityRecordsSectionRef"
              :doctor-station-id="stationDetails.doctorStation?.id ?? null"
              :activity-records-enabled="scheduleAcceptsActivityRecords"
              :can-send-record="!isSendRecordDisabled"
              :timezone="stationDetails.timezone"
              :winning-legs="activityWinningLegs"
              @send-record="handleSpeedDialAction('send-record')"
            />

            <!-- ==============================================================
                 §8.1 — o pacote de acordeões do M1. Entra INTEIRO ou não entra
                 (I19): enquanto `GET /stations/:id/registration` não responder,
                 nada disto existe e o Detalhes do Plantão é pixel a pixel o de
                 hoje. Com o backend no ar, "Local e biometria" deixa de ser
                 coluna do render antigo e vira bloco próprio, na ordem do M1.
                 ============================================================== -->
            <CollapsibleSection
              v-if="hasRegistrationAccordions && canReadDoctorCheck"
              title="Local e biometria"
              test-id="location-biometry-accordion"
              :touch-target="isFullScreen"
              :summary="isFullScreen ? mobileSummaries.locationBiometry : undefined"
              class="rounded-lg border border-lightGray30 px-3 py-3 md:px-4"
            >
              <div class="flex flex-wrap items-start gap-4 text-left">
                <div class="flex min-w-[190px] flex-col gap-2">
                  <span class="text-sm font-bold">{{ VERIFICATION_COLUMN_HEADER }}</span>
                  <v-btn
                    v-if="verificationButtonState !== 'NONE' && mainCheck"
                    :color="verificationButtonState === 'BIOMETRY' ? '#6B4FA3' : '#2F6F9F'"
                    depressed
                    class="station-action-btn !text-white"
                    data-testid="verification-button"
                    @click="openVerification(mainCheck.id)"
                  >
                    {{ VERIFICATION_BUTTON_LABELS[verificationButtonState] }}
                  </v-btn>
                  <span v-else class="text-sm text-gray-500" data-testid="verification-none">{{
                    VERIFICATION_BUTTON_LABELS.NONE
                  }}</span>
                </div>
                <div class="flex min-w-[140px] flex-col gap-2">
                  <span class="text-sm font-bold">Anexos do check</span>
                  <v-btn
                    v-if="mainCheck?.checkEvidences?.length"
                    color="primary"
                    depressed
                    class="station-action-btn"
                    @click="openEvidence(mainCheck.checkEvidences)"
                  >
                    {{ mainCheck.checkEvidences.length }} Arquivo{{
                      mainCheck.checkEvidences.length > 1 ? 's' : ''
                    }}
                  </v-btn>
                  <v-btn
                    v-else-if="isConciliationOrigin && attendanceStatus.containerId"
                    color="primary"
                    depressed
                    class="station-action-btn"
                    :loading="loadingDsEvidence"
                    @click="openDsAttendanceEvidence"
                  >
                    Visualizar
                  </v-btn>
                  <span v-else class="text-sm text-gray-500">Sem anexo</span>
                </div>
              </div>
            </CollapsibleSection>

            <!-- Montagem preguiçosa preservada: o corpo do `CollapsibleSection` é
                 `v-if`, então o `CheckAttemptsPanel` (que busca na API ao montar)
                 só nasce quando o gestor abre a seção.
                 Controlado por `showCheckAttempts`: o botão de tentativas no
                 header do modal e o cabeçalho do acordeão são o MESMO toggle, e o
                 botão fica aceso enquanto a seção estiver aberta. -->
            <div
              v-if="hasRegistrationAccordions"
              ref="checkAttemptsSectionRef"
              class="text-left"
              data-testid="station-details-check-attempts"
            >
              <CollapsibleSection
                v-model:expanded="showCheckAttempts"
                title="Tentativas de check"
                test-id="check-attempts-accordion"
                :touch-target="isFullScreen"
                class="rounded-lg border border-lightGray30 px-3 py-3 md:px-4"
              >
                <div class="rounded-lg border-[2px] border-primary p-2">
                  <CheckAttemptsPanel :source="checkAttemptsSource" compact />
                </div>
              </CollapsibleSection>
            </div>

            <!-- `hasStationStarted` também aqui: o alerta é irmão da seção, não
                 filho. Num plantão futuro `showDoctorCheck` é falso por
                 construção, então sem este gate sobraria um alerta de "sem
                 registro aprovado" cobrando um registro que ainda não podia
                 existir. -->
            <div
              v-if="attendanceStatusError"
              data-testid="attendance-status-error"
              class="text-negative text-sm text-left my-2"
            >
              {{ attendanceStatusError }}
            </div>

            <AttendanceAlert
              v-if="
                hasStationStarted &&
                !showDoctorCheck &&
                flags.enabledDsAttendancePolicy &&
                !attendanceStatusError &&
                !attendanceStatus.hasApprovedRecord
              "
              :date="formattedStartDate?.date"
              :icon="AlertIcon"
              :status="attendanceStatus.status"
              :is-overdue="attendanceStatus.isOverdue"
            />

            <!-- A avaliação desce por prop porque o modal já a buscou para decidir
                 o accordion acima — sem isso seriam dois GETs do mesmo endpoint.
                 O componente segue dono da própria renderização: nada aparece sem
                 flag, sem permissão ou quando o plantão não tem avaliação. -->
            <!-- I19 — no desktop, SEM acordeão de propósito. §8.1 pede sete blocos
                 em acordeão, "Local e biometria" e "Excepcionalidade" inclusos;
                 embrulhar só "Pós-plantão" e "Observações" mudaria a aparência
                 da tela para todo mundo no dia do deploy, sem feature detection
                 e sem entregar a reorganização que justificava a mudança. O
                 pacote entra inteiro, ou não entra.
                 Exceção: no celular (`useAccordions` via `isFullScreen`, R6) a
                 seção vira acordeão com resumo — mas só quando há o que ler
                 (flag + permissão); senão cai no `v-else`, que não renderiza nada. -->
            <CollapsibleSection
              v-if="showPostStationAccordion"
              title="Pós-plantão"
              test-id="post-station-accordion"
              :touch-target="isFullScreen"
              :summary="isFullScreen ? mobileSummaries.postStation : undefined"
              :default-expanded="false"
              class="rounded-lg border border-lightGray30 px-3 py-3 md:px-4"
            >
              <PostStationDetailsSection
                :station-id="stationDetails.id"
                :schedule-id="stationDetails.schedule.id"
                :evaluation="postStationEvaluation"
                :show-title="false"
                @sidebar-toggle="isPostStationSidebarOpen = $event"
              />
            </CollapsibleSection>
            <PostStationDetailsSection
              v-else
              :station-id="stationDetails.id"
              :schedule-id="stationDetails.schedule.id"
              :evaluation="postStationEvaluation"
              @sidebar-toggle="isPostStationSidebarOpen = $event"
            />

            <!-- A excepcionalidade continua morando no cadeado + sidebar; o
                 acordeão é só o resumo, para que a ordem do M1 não tenha um
                 buraco em plantão que TEM excepcionalidade. Sem excepcionalidade
                 o bloco não existe — não há o que resumir. -->
            <CollapsibleSection
              v-if="useAccordions && exceptionSummary"
              title="Excepcionalidade"
              test-id="exceptionality-accordion"
              :touch-target="isFullScreen"
              :summary="isFullScreen ? mobileSummaries.exceptionality : undefined"
              :default-expanded="false"
              class="rounded-lg border border-lightGray30 px-3 py-3 md:px-4"
            >
              <div class="flex flex-wrap items-center gap-3 text-left text-sm">
                <span data-testid="station-details-exceptionality-summary">{{
                  exceptionalitySummaryText
                }}</span>
                <button
                  type="button"
                  class="font-semibold text-primary30 underline"
                  data-testid="station-details-exceptionality-open"
                  @click="toggleSidebar('exceptionality')"
                >
                  Ver excepcionalidade
                </button>
              </div>
            </CollapsibleSection>
          </div>

          <!-- Tentativas de check (aceitas e rejeitadas) do plantão, entre o
               "Registro de plantão" e as "Observações". Montagem preguiçosa
               (`v-if`, nunca `v-show`): o painel busca na API ao montar, e
               ninguém deve pagar essa chamada só por abrir o detalhe. Sem modal
               sobre modal — a tabela e o mapa abrem aqui mesmo. -->
          <div
            v-if="showCheckAttempts && !hasRegistrationAccordions"
            ref="checkAttemptsSectionRef"
            class="text-left"
            data-testid="station-details-check-attempts"
          >
            <CollapsibleSection
              title="Tentativas de check"
              test-id="check-attempts-accordion"
              :touch-target="isFullScreen"
              class="rounded-lg border border-lightGray30 px-3 py-3 md:px-4"
            >
              <div class="rounded-lg border-[2px] border-primary p-2">
                <CheckAttemptsPanel :source="checkAttemptsSource" compact />
              </div>
            </CollapsibleSection>
          </div>

          <!-- I19 — idem "Pós-plantão": no desktop as Observações seguem
               exatamente como hoje enquanto o pacote de acordeões de §8.1 não
               vier inteiro; no celular (`useAccordions`, R6) viram acordeão com
               a contagem no resumo. -->
          <CollapsibleSection
            v-if="useAccordions && flags.enabledObservationsSystem && canReadObservations"
            title="Observações"
            test-id="observations-accordion"
            :touch-target="isFullScreen"
            :summary="isFullScreen ? mobileSummaries.observations : undefined"
            :default-expanded="false"
            class="rounded-lg border border-lightGray30 px-3 py-3 md:px-4"
          >
            <ObservationList
              :observations="formattedObservations"
              :show-title="false"
              @delete="handleDeleteObservation"
            />
          </CollapsibleSection>
          <ObservationList
            v-else-if="flags.enabledObservationsSystem && canReadObservations"
            :observations="formattedObservations"
            @delete="handleDeleteObservation"
          />
        </div>
      </div>
    </div>

    <template #footer></template>
  </DashboardModalShell>

  <!-- No celular os overlays vão para o #app DEPOIS do FullScreenPanel, senão os
       `fixed z-50` irmãos pintariam por baixo dele (docs/z-layers.md). No desktop
       nada muda: sem teleport. -->
  <MobileOverlayLayer :active="isFullScreen">
    <ExceptionalitiesSidebar
      v-model:is-open="isOpenExceptionalitySidebar"
      :doctor-station-id="Number(stationDetails?.doctorStation.id)"
      :schedule-id="Number(stationDetails?.schedule.id)"
      :station-context="stationContextData"
      :auto-open-exception-id="props.exceptionIdSelected"
      :post-station-locked="isPostStationLocked"
      @request-new="handleNewRequest"
      @action-completed-reload-data="fetchStationDetails"
    />
    <HistorySidebar
      v-model:is-open="isOpenHistorySidebar"
      :doctor-station-id="Number(stationDetails?.doctorStation.id)"
      @request-new="handleNewRequest"
    />

    <NewExceptionalityRequest
      v-if="stationDetails"
      v-model:is-open="isOpenRequestExceptionality"
      :original-start="stationDetails?.startHour"
      :original-end="stationDetails?.endHour"
      :timezone="stationDetails?.timezone"
      :proportionality-rule="stationDetails?.schedule?.proportionalityRule"
      :base-hourly-rate="baseHourlyRate || 0"
      :doctor-station-id="stationDetails?.doctorStation?.id"
      :original-in-cash="stationDetails?.doctorStation?.inCash"
      :schedule-id="Number(stationDetails?.schedule.id)"
      :station-context="stationContextData"
      :post-station-locked="isPostStationLocked"
      @requestCreated="fetchStationDetails"
    />

    <ManagementDoctorModal
      :showModal="isManagementDoctorModalOpen"
      :stationDetails="stationDetails"
      :mode="stationDetails?.doctorStation.doctor ? 'change' : 'allocate'"
      :initial-coverage="managementInitialCoverage"
      @closeModal="isManagementDoctorModalOpen = false"
      @allocationUpdated="fetchStationDetails"
    />

    <RemoveDoctorModal
      :showModal="isRemovedDoctorModalOpen"
      :stationDetails="stationDetails"
      :release="isReleaseMode"
      @closeModal="closeRemoveDoctorModal"
      @removalUpdated="fetchStationDetails"
    />

    <EvidenceModal
      v-model:is-open="isEvidenceModalOpen"
      :id="String(selectedEvidence?.id ?? '')"
      :file-name="selectedEvidence?.name ?? ''"
      :file="null"
      :file-url="evidenceBlobUrl ?? selectedEvidence?.path"
      :files="selectedEvidenceFiles"
      :uploader="evidenceUploader"
      :monitoring-type="mainCheck?.originLabel ?? ''"
      :source-app="attendanceStatus.sourceApp ?? ''"
      :status="DSAttendanceStatus.APPROVED"
      :history-events="historyEvents"
    />

    <CheckVerificationModal
      v-model:is-open="isVerificationModalOpen"
      :check-id="verificationCheckId"
    />

    <!--
    F4 — "Ver evidências" da seção de registro. IRMÃO do Detalhes do Plantão, nunca
    filho: a raiz deste modal ganha `transform` quando uma sidebar abre, e um
    `position: fixed` descendente passa a resolver contra ela — o modal nasceria
    deslocado 14rem. O `v-if` também é o que desmonta os painéis ao fechar, e é a
    desmontagem que revoga as blob URLs dos arquivos baixados.
  -->
    <StationEvidenceModal
      v-if="isStationEvidenceModalOpen && stationEvidenceContext"
      v-model="isStationEvidenceModalOpen"
      :context="stationEvidenceContext"
    />

    <!--
    M2 — "Enviar registro". Montado só depois do clique (`v-if`) e só quando a
    vaga tem alocação: sem `doctorStationId` não existe registro a enviar.
    A W4 emite `saved` e `submitted` com o MESMO payload (um é alias do outro);
    ouvimos só `saved`, senão a recarga sairia em dobro.
  -->
    <SendRecordModal
      v-if="isSendRecordModalOpen && sendRecordContext"
      v-model="isSendRecordModalOpen"
      :context="sendRecordContext"
      :doctor-station-id="sendRecordContext.doctorStationId"
      :schedule-id="sendRecordContext.scheduleId"
      @saved="handleSendRecordSaved"
      @redirect-to-sheet="handleRedirectToSheet"
    />

    <!--
    C6 — "Registro de Plantão" no passo 1 do SendRecordModal. O wizard de folha abre AQUI,
    com os campos da vaga já preenchidos e travados, em vez de mandar o usuário para a fila
    em outra aba e obrigá-lo a redigitar empresa, unidade, data e escala.
    Montado só depois do clique, pela mesma razão do SendRecordModal.
  -->
    <AttendanceUploadModal
      v-if="isAttendanceUploadModalOpen"
      v-model="isAttendanceUploadModalOpen"
      :initial-form="sheetInitialForm"
      :locked-fields="sheetLockedFields"
      @save="handleSendRecordSaved"
    />
  </MobileOverlayLayer>
</template>

<script lang="ts" setup>
import { ref, watch, computed, nextTick, defineAsyncComponent } from 'vue'
import { useToast } from 'vue-toastification'
import { getApiErrorMessage } from '@/utils/apiError'
import { useErrorToast } from '@/composables/useErrorToast'
import PadlockButton from '@/components/designSystem/Buttons/PadlockButton/Index.vue'
import NetworkService from '@/Network/index'
import CardDutyFee from '@/components/designSystem/Cards/CardDutyFee.vue'
import CardDutyWithDoctor from '@/components/designSystem/Cards/CardDutyWithDoctor.vue'
import StationDetailsSkeleton from '@/components/designSystem/Skeleton/StationDetailsSkeleton.vue'
import StationDetailsHeaderSkeleton from '@/components/designSystem/Skeleton/StationDetailsHeaderSkeleton.vue'
import EditPencilIcon from '@/assets/icons/pencil.vue'
import AccountSwitchIcon from '@/assets/icons/account-switch.vue'
import { timezones } from '@/hooks/useCommon'
import ObservationList from '@/components/ObservationList.vue'
import ExceptionalitiesSidebar from '../sidebars/ExceptionalitiesSidebar.vue'

import NewExceptionalityRequest from '../sidebars/NewExceptionalityRequest.vue'
import { differenceInHours } from 'date-fns'
import { DoctorStationObservationListResponse } from '@/Network/Types/Responses/DsObservationResponse'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { CheckEvidence } from '@/Network/Types/Requests/StationRequests'
import { ShiftDetails } from '@/Network/Types/Responses/StationResponses'
import { hasInstantPassed } from '@/utils/date'
import { getProportionalityRuleTitle } from '@/utils/schedule'
import { getVerificationButtonState } from '@/utils/check-verification'
import {
  VERIFICATION_BUTTON_LABELS,
  VERIFICATION_COLUMN_HEADER
} from '@/constants/check-verification.constants'
import { getCheckOriginLabel } from '@/utils/doctorStation'
import { mapAuditTrailToHistoryEvents } from '@/utils/dsAttendanceHistory'

import CheckAttemptsButton from '@/components/designSystem/Buttons/CheckAttemptsButton/index.vue'
import CheckAttemptsPanel from '@/components/CheckAttempts/CheckAttemptsPanel.vue'
import CollapsibleSection from './CollapsibleSection.vue'
import DashboardModalShell from '../mobile/DashboardModalShell.vue'
import MobileOverlayLayer from '../mobile/MobileOverlayLayer.vue'
import StationDetailsDock from '../mobile/StationDetailsDock.vue'
import {
  useStationDetailActions,
  type StationDetailActionsVariant
} from '../composables/useStationDetailActions'
import { resolveStationLegLock } from '../composables/useStationLegLock'
import ValueDetailsSection from './ValueDetailsSection.vue'
import RepassProportionalityDetailsSection from './RepassProportionalityDetailsSection.vue'
import WorkJourneyDetailsSection from './WorkJourneyDetailsSection.vue'
import PostStationDetailsSection from './PostStationDetailsSection.vue'
import StationRegistrationSection from './StationRegistrationSection.vue'
import type { StationRegistrationView } from './StationRegistrationSection.vue'
import StationRequiredCheckLine from './StationRequiredCheckLine.vue'
import { useRouter } from 'vue-router'
import StationActivityRecordsSection from './StationActivityRecordsSection.vue'
import StationRecordsSection from './StationRecordsSection.vue'
import { useCanReadAttendance } from '@/modules/company/Approvals/DsAttendance/composables/useCanReadAttendance'
import type {
  RedirectToSheetPayload,
  SendRecordContext
} from '@/components/designSystem/modals/SendRecordModal/types'
import { buildSheetHandoff } from '@/components/designSystem/modals/SendRecordModal/types'
import type { DsAttendanceUploadForm } from '@/components/designSystem/modals/AttendanceUploadModal/types'

import {
  ExceptionStatusEnum,
  ExceptionSummary
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import ExceptionService from '@/Network/Services/DoctorStationException'
import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import HistorySidebar from '@/components/Sidebars/HistorySidebar.vue'
import AccountRemove from '@/assets/icons/account-remove.vue'
import DotsHorizontal from '@/assets/icons/dots-horizontal.vue'
import AccountPlus from '@/assets/icons/account-plus.vue'
import ManagementDoctorModal from '../modals/managementDoctor/management.doctor.modal.vue'
import RemoveDoctorModal from '../modals/removeDoctor/remove.doctor.modal.vue'
import UnidentifiedCoverageDetails from './UnidentifiedCoverageDetails.vue'
import OptionalCollapsible from './OptionalCollapsible.vue'
import { STATUS_CONFIG, UNKNOWN_STATUS_CONFIG } from '@/modules/company/PostStation/constants'
import { PostStationStatus } from '@/modules/company/PostStation/types'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import { canMarkUnidentified, getStationCoverageInfo } from '@/utils/coverage'
import { addHours, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import EvidenceModal from './EvidenceModal.vue'
import CheckVerificationModal from './CheckVerificationModal.vue'
import StationEvidenceModal from './StationEvidenceModal.vue'
import type { StationEvidenceContext } from '@/types/station-evidence.types'
import { DSAttendanceStatus, DSAttendanceAuditType } from '@/constants/ds-attendance.constants'
import { HistoryEvent } from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import AlertIcon from '@/assets/icons/mdiAlertCircle.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { mdiLockOpenVariant, mdiUpload } from '@mdi/js'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { CoveredLegs } from '@/constants/ds-attendance.constants'
import AttendanceAlert from '@/components/designSystem/Alerts/AttendanceAlert.vue'
import HistoryButton from '@/components/designSystem/Buttons/ViewChangeHistoryButton/index.vue'
import StationIdBadge from '@/components/designSystem/Badges/StationIdBadge.vue'
import { getPermanenceVisibility, shouldRefetchOnStationIdChange } from '@/utils/permanence'
import {
  usePostStationLock,
  type UsePostStationLock
} from '@/modules/company/PostStation/composables/usePostStationLock'

/**
 * M2 — o modal de envio é PESADO (5 passos, dropzone e visualizador) e serve uma
 * minoria dos plantões. Assíncrono + `v-if`: quem nunca clica em "Enviar
 * registro" não baixa nem executa a árvore da W4, e os specs que já montam este
 * modal continuam sem conhecê-la.
 */
const SendRecordModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/SendRecordModal/Index.vue')
)
const AttendanceUploadModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/AttendanceUploadModal/Index.vue')
)

export type StationDetailsContext = 'dashboard' | 'approvals'

export type ApprovalStatus = 'Approved' | 'Rejected' | null
export interface ApprovalStatusMap {
  financials: ApprovalStatus
  repassMode: ApprovalStatus
  proportionalityRule: ApprovalStatus
  workJourney: ApprovalStatus
}

const { flags } = useFeatureFlags()
const isEnabledException = () => flags.enabledManagementExceptionalities
const isEnabledStationHistory = () => flags.enabledStationHistory

const props = withDefaults(
  defineProps<{
    showModal: boolean
    infos: { stationId: number }
    mode?: StationDetailsContext
    isPushedExternally?: boolean
    exceptionIdSelected?: any
    approvalStatusMap?: ApprovalStatusMap
    /**
     * R6 (spec 2026-09-30): tela cheia no celular. O IndexV2 liga com `isMobileLayout`;
     * os outros consumidores (Aprovações, Consulta, DsAttendance) não passam — a tela
     * cheia não vaza para páginas fora do escopo do mobile.
     */
    mobileShell?: boolean
    /**
     * Trava de Pós-Plantão já mantida pelo consumidor (Excepcionalidades): o
     * modal usa esta instância e NÃO sonda de novo — quem sonda é o consumidor,
     * 1 GET por clique. Sem a prop, o modal cria e sonda a própria.
     */
    postStationLock?: UsePostStationLock
  }>(),
  {
    mode: 'dashboard',
    isPushedExternally: false,
    mobileShell: false,
    approvalStatusMap: () => ({
      financials: null,
      repassMode: null,
      proportionalityRule: null,
      workJourney: null
    })
  }
)

const emit = defineEmits(['closeModal', 'editStation', 'navigateToStation', 'sendRecord'])

const permissionsStore = usePermissionsStore()
const dsAttendanceStore = useDsAttendanceStore()
const router = useRouter()

const scheduleId = computed(() => stationDetails.value?.schedule?.id)

const canAllocateDoctor = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationDoctorPackageAllocating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const checkSchedulePermission = (permission: AuthorizationPermissions) => {
  if (!scheduleId.value) return false
  return permissionsStore.check({
    permission: permission,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: scheduleId.value
    }
  })
}

const canReadExceptionalities = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationExceptionalitiesReading)
)
const canReadHistory = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationEditionHistoricalReading)
)
const canEditStation = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationEditing)
)
const canRemoveDoctor = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationDoctorPackageRemoving)
)
const canReadPaymentRules = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationPaymentRulesReading)
)
const canReadAntecipe = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationAntecipeReading)
)
const canReadScheduleRequirements = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.ScheduleRequerimentReading)
)
const canReadProportionalityRule = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.ScheduleProportionalityRuleReading)
)
const canReadDoctorCheck = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationDoctorCheckReading)
)
// Spec 2026-09-24 §7.1 — a lista de registros exige `attendance_reading` no ds_attendance;
// `canReadDoctorCheck` é permissão de escala e não a implica.
const canReadAttendance = useCanReadAttendance()

const canReadObservations = computed(() =>
  checkSchedulePermission(AuthorizationPermissions.StationObservationsReading)
)

/**
 * "Enviar registro" (M1, balão ⑦). TRÊS condições independentes, com mensagens
 * diferentes de propósito: sem a permissão de EMISSÃO não adianta configurar a
 * escala, com a permissão mas a escala recusando Registro de Atividade o
 * caminho é a política, e com os dois OK falta o plantão ter terminado (Q35) —
 * o tooltip precisa dizer qual dos três é o caso.
 *
 * `checkAnyRessources` e não `check`: as permissões de `ds_attendance` são
 * resolvidas pelo grafo (empresa → unidade → escala) e este modal não conhece o
 * id do recurso `ds_attendance`. É o mesmo caminho que a fila de auditoria já
 * usa em `canRevertValidation`.
 */
const canEmitActivityRecord = computed(() =>
  permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.AttendanceEmitting,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendance
  })
)

const scheduleAcceptsActivityRecords = computed(() => dsAttendanceStore.canSendActivityRecord)

/**
 * RN-07 / RF-12: trava "Alterar/Alocar médico" e o lápis de "Editar Plantão"
 * (variante sem smart allocation) quando o plantão tem avaliação Aprovada —
 * mesma decisão do `update.station.modal`. Sem permissão de leitura do status a
 * avaliação fica `null` e a UI não trava: a barreira real, nesse caso, é o
 * backend (`POST_STATION_EVALUATION_APPROVED`).
 *
 * `evaluation` também desce por prop para o `PostStationDetailsSection`,
 * evitando um segundo GET do mesmo endpoint.
 *
 * Ocupado sem identificação não tem médico a avaliar (a UI mostra "Não
 * avaliado"): sem `stationId` o composable não sonda e evita o 404 inútil.
 * Plantão futuro, em andamento ou sem médico também não sonda (`eligibility`):
 * a avaliação só existe depois do fim do plantão.
 *
 * Com `postStationLock` vindo do consumidor, a instância dele é usada e a
 * sondagem fica com ele (lido uma vez no setup: a prop não troca com o modal
 * montado).
 */
const injectedPostStationLock = props.postStationLock
const {
  isPostStationLocked,
  evaluation: postStationEvaluation,
  canReadPostStation,
  refresh: refreshOwnPostStationLock
} = injectedPostStationLock ??
usePostStationLock(
  () => (isUnidentifiedStation.value ? null : props.infos?.stationId),
  () => scheduleId.value,
  () => ({
    endHour: stationDetails.value?.endHour,
    hasDoctor: !!stationDetails.value?.doctorStation
  })
)
const refreshPostStationLock = () =>
  injectedPostStationLock ? Promise.resolve() : refreshOwnPostStationLock()

const isManagementDoctorModalOpen = ref(false)
const isRemovedDoctorModalOpen = ref(false)
// M5-B — modo inicial do modal de alocação e se o de remoção abre como "Liberar vaga".
const managementInitialCoverage = ref<CoverageMode | null>(null)
const isReleaseMode = ref(false)

const coverageInfo = computed(() => getStationCoverageInfo(stationDetails.value))
const isUnidentifiedStation = computed(
  () => coverageInfo.value.status === CoverageStatus.UNIDENTIFIED
)
// Q30-A: com a flag da escala desligada o estado continua visível, mas não se
// edita o responsável (isso seria marcar de novo); identificar e liberar seguem.
const canEditUnidentifiedResponsible = computed(
  () => canAllocateDoctor.value && canMarkUnidentified(stationDetails.value?.schedule)
)
const showCoverageActions = computed(
  () => props.mode === 'dashboard' && flags.enabledSmartAllocation
)
// `referenceValue` só vem com a feature no BE; sem ele (BE antigo) cai em `value`.
// Os dois nulos → "—" no card, sem inventar zero.
const unidentifiedReferenceValue = computed(
  () => stationDetails.value?.referenceValue ?? stationDetails.value?.value ?? null
)
// Mesmo formatador do badge do tipo (`formattedStationType`); sem tipo → sem rótulo no card.
const unidentifiedStationTypeLabel = computed(() =>
  formatStationTypeLabel(stationDetails.value?.type ?? stationDetails.value?.doctorStation?.type)
)

function openManagement(coverage: CoverageMode | null) {
  managementInitialCoverage.value = coverage
  isManagementDoctorModalOpen.value = true
}

function openRelease() {
  isReleaseMode.value = true
  isRemovedDoctorModalOpen.value = true
}

function closeRemoveDoctorModal() {
  isRemovedDoctorModalOpen.value = false
  isReleaseMode.value = false
}
const isSpeedDialOpen = ref(false)

/**
 * Seção "Tentativas de check": nasce recolhida e só monta o painel no clique do
 * botão do header. O `source` é um computed (e não um objeto literal no
 * template) porque o `CheckAttemptsPanel` observa a prop com `deep: true` — um
 * literal recriado a cada re-render dispararia uma busca nova a cada render.
 */
const showCheckAttempts = ref(false)
const checkAttemptsSectionRef = ref<HTMLElement | null>(null)
const checkAttemptsSource = computed(() => ({ stationId: Number(props.infos?.stationId) }))

// No modo com acordeões a seção fica longe do header (depois de Registro,
// Atividades e Local): ao abrir pelo botão, rola até ela para o clique ter
// efeito visível. `scrollIntoView?.` porque o jsdom dos testes não a implementa.
function toggleCheckAttempts() {
  showCheckAttempts.value = !showCheckAttempts.value
  if (!showCheckAttempts.value) return
  nextTick(() => {
    checkAttemptsSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  })
}

// Trocar o plantão exibido recolhe a seção: o painel busca por `stationId` e
// deixá-lo aberto mostraria as tentativas do plantão anterior enquanto o novo
// detalhe ainda carrega.
watch(
  () => props.infos?.stationId,
  () => {
    showCheckAttempts.value = false
  }
)

function toggleSpeedDial() {
  isSpeedDialOpen.value = !isSpeedDialOpen.value
}

// M1 (Q23): "Enviar registro" é ação PRIMÁRIA e vem PRIMEIRO. Os três
// pontinhos continuam com Editar/Remover/Histórico — o usuário não precisa
// abrir a edição do plantão para enviar um comprovante.
type SpeedDialAction = 'send-record' | 'edit' | 'remove' | 'history'

function handleSpeedDialAction(action: SpeedDialAction) {
  switch (action) {
    case 'send-record':
      // O emit continua saindo (quem monta este modal pode querer reagir), mas o
      // hub de M2 abre AQUI: é neste componente que moram a vaga, a escala e as
      // seções de registro que precisam recarregar depois do envio.
      emit('sendRecord', stationDetails.value)
      openSendRecordModal()
      break
    case 'edit':
      emit('editStation', stationDetails.value)
      break
    case 'history':
      toggleSidebar('history')
      break
    case 'remove':
      isReleaseMode.value = false
      isRemovedDoctorModalOpen.value = true
      break
  }
  isSpeedDialOpen.value = false
}

const isOpenExceptionalitySidebar = ref(false)
const isOpenHistorySidebar = ref(false)
// Alimentado pelo emit do PostStationDetailsSection: a sidebar de pós-plantão
// desliza o modal como as de excepcionalidade e histórico.
const isPostStationSidebarOpen = ref(false)
const stationDetails = ref<ShiftDetails | null>(null)

const exceptionSummary = ref<ExceptionSummary | null>(null)

/**
 * F2 — o registro da vaga por perna (`GET /stations/:id/registration`).
 *
 * `null` tem DOIS significados que a seção trata igual: a rota ainda não foi
 * deployada (o service devolve `null` em 404/405/501) ou o plantão não tem
 * registro nenhum. Nos dois casos a seção cai no render de hoje — a tela nunca
 * quebra por causa de um backend mais antigo que o web.
 */
const stationRegistration = ref<StationRegistrationView | null>(null)
const registrationLoadError = ref<string | null>(null)

/**
 * Trava por perna (backend #3055) do registro acima. Sem registro (rota ausente,
 * sem permissão de leitura de check) = destravado: o backend segue recusando.
 */
const stationLegLock = computed(() => resolveStationLegLock(stationRegistration.value))

const loading = ref(true)
const errorMessage = ref<string | null>(null)
const lastFetchedStationId = ref<number | null>(null)
const observations = ref<DoctorStationObservationListResponse>({ data: [], count: 0 })

const isOpenRequestExceptionality = ref(false)
const isEvidenceModalOpen = ref(false)
const selectedEvidence = ref<CheckEvidence | null>(null)
const selectedEvidenceFiles = ref<CheckEvidence[]>([])
const toast = useToast()
const { notifyError } = useErrorToast()
const loadingDsEvidence = ref(false)
const historyEvents = ref<HistoryEvent[]>([])
const evidenceUploader = ref('')

const evidenceBlobUrl = ref<string | undefined>(undefined)

const isVerificationModalOpen = ref(false)
const verificationCheckId = ref<number | null>(null)

// A verificação (foto de biometria + mapa) é buscada pelo próprio
// `CheckVerificationModal` a partir do id do check — não depende dos anexos
// baixados aqui, que continuam sendo apenas os arquivos da coluna "Evidência".
function openVerification(checkId: number) {
  verificationCheckId.value = checkId
  isVerificationModalOpen.value = true
}

async function fetchHistoryEvents(containerId: number) {
  try {
    const response = await NetworkService.dsAttendance.getAuditTrail(containerId)
    historyEvents.value = mapAuditTrailToHistoryEvents(response.data)
    const uploadEvent = response.data.find(
      (item) => item.actionType === DSAttendanceAuditType.EVIDENCE_UPLOAD
    )
    if (uploadEvent?.updaterUser?.name) {
      evidenceUploader.value = uploadEvent.updaterUser.name
    }
  } catch (error) {
    // Antes só o console sabia: o histórico ficava vazio como se o registro não
    // tivesse eventos, sem distinguir "sem histórico" de "falhou ao buscar".
    console.error('Erro ao buscar histórico do container:', error)
    notifyError(error, 'Não foi possível carregar o histórico do registro de presença.')
  }
}
const exceptionService = new ExceptionService()
const attendanceStatus = ref<{
  hasApprovedRecord: boolean
  containerId?: number
  status?: string
  isOverdue?: boolean
  sourceApp?: string | null
}>({ hasApprovedRecord: false })

/**
 * Mensagem de falha da checagem de presença. Quando preenchida, o alerta de
 * "sem registro aprovado" NÃO é exibido: sem resposta do backend não dá para
 * afirmar que falta registro — o usuário vê o erro e não uma cobrança falsa.
 */
const attendanceStatusError = ref<string | null>(null)

const formattedStartDate = computed(() =>
  formatDate(stationDetails.value?.startHour, stationDetails.value?.timezone)
)
const formattedEndDate = computed(() =>
  formatDate(stationDetails.value?.endHour, stationDetails.value?.timezone)
)

function nameOrEmpty(value?: string | null): string {
  return value || ''
}

function formatDoctorCrm(crm?: { number?: string; uf?: string } | null): string | undefined {
  if (!crm) return undefined
  return `${nameOrEmpty(crm.number)}-${nameOrEmpty(crm.uf)}`
}

// O template montava `${number}-${uf}` inline e, sem CRM, produzia um "-" solto
// sob o nome do médico (o `DoctorInfo` só esconde o subtítulo se ele for falsy,
// e "-" é truthy). String vazia faz o `v-if="subtitle"` esconder a linha.
// Reusa `formatDoctorCrm` em vez de repetir a concatenação.
const doctorCrmLabel = computed(() => {
  const crm = stationDetails.value?.doctorStation?.doctor?.mainCrm
  if (!crm?.number && !crm?.uf) return ''
  return formatDoctorCrm(crm) ?? ''
})

function formatCurrency(value?: number | null): string | undefined {
  if (value == null) return undefined
  return `R$ ${Number(value).toLocaleString('pt-BR')}`
}

function formatRepassMode(inCash?: boolean | null): string | undefined {
  if (inCash == null) return undefined
  return inCash ? 'À vista' : 'Fluxo'
}

function formatAllocatedBy(
  sponsor?: { name?: string; registration?: string | null } | null
): string | undefined {
  if (!sponsor?.name) return undefined
  const suffix = sponsor.registration ? ` #${sponsor.registration}` : ''
  return `${sponsor.name}${suffix}`
}

function formatTimeDetail(time: FormattedDateTime | null): string | undefined {
  if (!time) return undefined
  return `${time.weekday}, ${time.date} às ${time.time}`
}

function formatTimeRange(start: FormattedDateTime | null, end: FormattedDateTime | null): string {
  if (!start || !end) return ''
  return `${start.date} ${start.time} - ${end.date} ${end.time}`
}

function formatTeam(ourTeam?: boolean | null): string {
  return ourTeam ? 'Interno' : 'Externo'
}

const stationContextData = computed(() => {
  if (!stationDetails.value) return undefined
  const sd = stationDetails.value
  const doctor = sd.doctorStation?.doctor
  const crm = doctor?.mainCrm
  const startTime = formattedStartDate.value
  const endTime = formattedEndDate.value
  const sponsor = sd.doctorStation?.userSponsor

  return {
    doctorName: nameOrEmpty(crm?.name),
    doctorCrm: formatDoctorCrm(crm),
    scheduleName: nameOrEmpty(sd.schedule?.name),
    positionName: nameOrEmpty(sd.position?.name),
    shiftName: nameOrEmpty(sd.shift?.name),
    team: formatTeam(sd.doctorStation?.ourTeam),
    timeRange: formatTimeRange(startTime, endTime),
    dutyType: nameOrEmpty(sd.doctorStation?.type),
    value: formatCurrency(sd.value),
    formation: nameOrEmpty(sd.doctorStation?.dsPaymentDoctorFormation?.name),
    repassMode: formatRepassMode(sd.doctorStation?.inCash),
    proportionalityRule: nameOrEmpty(proportionalityRuleTitle.value),
    breadcrumb: [
      sd.schedule?.healthUnit?.company?.name,
      sd.schedule?.healthUnit?.name,
      sd.schedule?.name,
      sd.position?.name
    ]
      .filter(Boolean)
      .join(' > '),
    allocatedBy: formatAllocatedBy(sponsor),
    startDetail: formatTimeDetail(startTime),
    endDetail: formatTimeDetail(endTime),
    timezone: getTimezoneTitle(sd.timezone) || undefined
  }
})

function toggleSidebar(sidebarName: 'history' | 'exceptionality' | 'edit') {
  isSpeedDialOpen.value = false

  const wasHistoryOpen = isOpenHistorySidebar.value
  const wasExceptionalityOpen = isOpenExceptionalitySidebar.value

  isOpenHistorySidebar.value = false
  isOpenExceptionalitySidebar.value = false
  isOpenRequestExceptionality.value = false

  switch (sidebarName) {
    case 'history':
      if (!wasHistoryOpen) {
        isOpenHistorySidebar.value = true
      }
      break
    case 'exceptionality':
      if (!wasExceptionalityOpen) {
        isOpenExceptionalitySidebar.value = true
      }
      break
    case 'edit':
      emit('editStation', stationDetails.value)
      break
  }
}

function handleNewRequest() {
  isOpenExceptionalitySidebar.value = false
  isOpenRequestExceptionality.value = true
}

// Invariante: modal fechado ⇒ nenhuma sidebar filha aberta. As sidebars são
// irmãs do `<ModalVue>` no template, com estado local próprio — esconder o
// modal não as esconde.
function closeAllSidebars() {
  isOpenExceptionalitySidebar.value = false
  isOpenHistorySidebar.value = false
  isOpenRequestExceptionality.value = false
  isPostStationSidebarOpen.value = false
}

const proportionalityRuleTitle = computed(() => {
  if (exceptionSummary.value?.changes.proportionalityRule?.requested) {
    return getProportionalityRuleTitle(
      exceptionSummary.value.changes.proportionalityRule.requested as ProportionalityRuleEnum
    )
  }
  return getProportionalityRuleTitle(stationDetails.value?.schedule?.proportionalityRule)
})

const getTimezoneTitle = (value: string): string | null => {
  const found = timezones.find((tz) => tz.value === value)
  return found ? found.title : null
}

const cards = [
  { formation: 'Residência ano 3', value: 500, repassMode: 'Fluxo' },
  { formation: 'Clínica Médica', value: 450, repassMode: 'Fixo' },
  { formation: 'Pediatria', value: 550, repassMode: 'Variável' }
]

function formatStationTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
}

const formattedStationType = computed(
  () => formatStationTypeLabel(stationDetails.value?.type) ?? 'Aberta'
)

const stationTypeClass = computed(() => {
  switch (stationDetails.value?.type) {
    case 'SEMANAL':
      return 'station-semanal'
    case 'COBERTURA':
      return 'station-cobertura'
    case 'EXTRA':
      return 'station-extra'
    default:
      return 'station-aberta'
  }
})

function close() {
  emit('closeModal')
}

/**
 * As duas flags legadas da escala, só usadas na transição (fonte `SCHEDULE`). `null`
 * quando a escala ainda não chegou: sem elas a linha degrada em vez de estourar.
 */
const scheduleCheckFlags = computed(() => {
  const schedule = stationDetails.value?.schedule
  if (!schedule) return null
  return {
    ourTeam: Boolean(schedule.requiredCheckOurTeam),
    notOurTeam: Boolean(schedule.requiredCheckNotOurTeam)
  }
})

/**
 * M7, balão ⑯: "ver política" / "criar política" leva à escala do plantão já aberta na
 * aba Registro de plantão — um clique entre a pergunta e a resposta. Fecha o modal
 * antes de navegar para o gestor não voltar com o detalhe sobreposto à outra tela.
 */
function openSchedulePolicy() {
  const targetScheduleId = scheduleId.value
  if (!targetScheduleId) return

  close()
  // `useRouter()` em vez de `import { router } from '@/router'`: importar a instância
  // puxa o grafo inteiro de rotas só para navegar daqui, e isso quebra os testes de
  // unidade que montam este modal isoladamente.
  router?.push({
    path: '/gerenciar-escala',
    query: { openScheduleId: String(targetScheduleId), editTab: 'registro-de-plantao' }
  })
}

function handleDeleteObservation(index: number) {
  console.log(index)
}

interface FormattedDateTime {
  weekday: string
  date: string
  time: string
}

function formatDate(dateString?: string, timezone: string = '-03:00'): FormattedDateTime | null {
  if (!dateString) return null

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null

    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: timezone })

    const formattedDate = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: timezone
    })

    const formattedTime = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    })

    return {
      weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
      date: formattedDate,
      time: formattedTime
    }
  } catch (e) {
    console.error('Error formatting date:', e)
    return null
  }
}

const mainCheck = computed(() => {
  const doctorChecks = stationDetails.value?.doctorStation?.doctorChecks

  const check = doctorChecks?.length === 1 ? doctorChecks[0] : doctorChecks?.find((c) => c.isMain)

  if (!check) return null

  const checkEvidences = check.checkEvidences || []

  return { ...check, originLabel: getCheckOriginLabel(check.origin), checkEvidences }
})

// Decisão de visibilidade (chips + linhas de vínculo) extraída como função pura
// testada (`getPermanenceVisibility`, tests/unit/permanence-utils.spec.ts) em
// vez de `&&` inline no template — este arquivo não tem mount test (ver
// "Notas de divergência" #6 do plano), então o cenário de flag desligada só
// fica coberto se a decisão em si for uma função pura.
const permanenceVisibility = computed(() =>
  getPermanenceVisibility(flags.enabledPermanenceRecord, mainCheck.value)
)

const isConciliationOrigin = computed(
  () => mainCheck.value?.origin === 'FOLHA' || mainCheck.value?.origin === 'EXTERNAL_APP'
)

/**
 * Feature detection do pacote de acordeões (§8.1 + I19). O sinal é a resposta de
 * `GET /stations/:id/registration`: com ela, o Registro de Plantão já é o render
 * por perna e "Local e biometria" deixou de ser coluna dele — os sete blocos
 * fazem sentido. Sem ela (404 do backend antigo, ou sem permissão de leitura do
 * check) o Detalhes do Plantão é EXATAMENTE o de hoje, pixel a pixel: o pacote
 * entra inteiro ou não entra.
 */
const hasRegistrationAccordions = computed(() => !!stationRegistration.value)

/**
 * Pós-plantão, Excepcionalidade e Observações em acordeão: com o pacote §8.1 (I19)
 * como hoje, OU no celular — lá a tela é longa e toda seção vira acordeão (R6).
 * "Local e biometria" e "Tentativas de check" seguem só com o pacote: sem ele, a
 * biometria é coluna do Registro de Plantão e virar bloco próprio a duplicaria.
 */
const useAccordions = computed(() => hasRegistrationAccordions.value || isFullScreen.value)

/**
 * Pós-plantão em acordeão. Com o pacote §8.1 segue como hoje (desktop intacto); no
 * celular só quando o gestor pode ler o pós-plantão (flag + permissão) — sem isso
 * o `PostStationDetailsSection` não renderiza nada e o acordeão ficaria vazio.
 */
const showPostStationAccordion = computed(
  () => hasRegistrationAccordions.value || (isFullScreen.value && canReadPostStation.value)
)

/**
 * Valor exibido pelo `ValueDetailsSection` no dashboard: o excepcional quando há
 * excepcionalidade com valor, senão o do plantão. O resumo do celular usa o mesmo.
 */
const valuePanelOriginalValue = computed(() =>
  props.mode === 'dashboard' && exceptionSummary.value?.financials?.totalExceptionalValue != null
    ? Number(exceptionSummary.value.financials.totalExceptionalValue)
    : stationDetails.value?.value
)

/**
 * Há algo legível no painel de valores (mesmas permissões do `ValueDetailsSection`)?
 * Sem isso, no celular o acordeão "Valores do plantão" não aparece (só o painel vazio,
 * como no desktop).
 */
const hasReadableValues = computed(() => {
  if (canReadPaymentRules.value) return true
  const ds = stationDetails.value?.doctorStation
  return canReadAntecipe.value && !!(ds?.requestAntecipe || ds?.antecipeWasPaid)
})

/** Rótulos do status da excepcionalidade — os mesmos do `StatusBadge` da sidebar. */
const EXCEPTION_STATUS_LABELS: Record<ExceptionStatusEnum, string> = {
  [ExceptionStatusEnum.AWAITING_APPROVAL]: 'Em andamento',
  [ExceptionStatusEnum.APPROVED]: 'Aprovado',
  [ExceptionStatusEnum.REJECTED]: 'Reprovado',
  [ExceptionStatusEnum.REVERTED]: 'Revertido',
  [ExceptionStatusEnum.CANCELED]: 'Cancelado'
}

/** Resumo de "Valores do plantão": só com a permissão; mesmo texto do painel sem valor. */
const valuesSummary = computed(() =>
  canReadPaymentRules.value
    ? (formatCurrency(valuePanelOriginalValue.value) ?? 'Não calculado')
    : undefined
)

const exceptionalitySummaryLabel = computed(() => {
  const status = exceptionSummary.value?.status
  return status ? (EXCEPTION_STATUS_LABELS[status] ?? status) : undefined
})

/** Resumos dos cabeçalhos dos acordeões no celular. */
const mobileSummaries = computed(() => {
  const sd = stationDetails.value
  const status = postStationEvaluation.value?.consolidatedStatus as PostStationStatus | undefined
  const observationCount = observations.value.data.length
  return {
    doctor: isUnidentifiedStation.value
      ? 'Sem identificação'
      : sd?.doctorStation?.doctor?.mainCrm?.name || 'Vaga aberta',
    values: valuesSummary.value,
    locationBiometry: mainCheck.value ? undefined : 'pendente',
    postStation: status ? (STATUS_CONFIG[status] ?? UNKNOWN_STATUS_CONFIG).label : 'Não avaliado',
    exceptionality: exceptionalitySummaryLabel.value,
    observations: observationCount ? String(observationCount) : undefined
  }
})

/** Botão da coluna "Local e biometria" — mesma regra pura do render antigo. */
const verificationButtonState = computed(() => getVerificationButtonState(mainCheck.value))

/**
 * Qual container venceu cada perna. Sai do registro por perna e desce para os
 * cards: um Registro de Atividade APROVADO pode ter perdido o ranking para a
 * folha, então "venceu a perna de X" nunca pode ser deduzido do status do card.
 */
const activityWinningLegs = computed(() => {
  const legs = stationRegistration.value?.raw?.legs
  return {
    checkin: legs?.checkin?.source?.dsAttendanceContainerId ?? null,
    checkout: legs?.checkout?.source?.dsAttendanceContainerId ?? null
  }
})

/** Resumo de uma linha da excepcionalidade — o detalhe continua na sidebar. */
const exceptionalitySummaryText = computed(() => {
  const summary = exceptionSummary.value
  if (!summary) return ''
  const requester = summary.requester?.name ? ` · solicitada por ${summary.requester.name}` : ''
  return `Excepcionalidade ${summary.status}${requester}.`
})

// Gate do "Registro de plantão" e do alerta de presença: antes do início
// teórico não existe registro a exibir nem a cobrar.
const hasStationStarted = computed(() => hasInstantPassed(stationDetails.value?.startHour))

// Só decide o TEXTO do check-out ausente (não gateia nada): plantão em andamento
// mostra "Aguardando", plantão encerrado mostra "Não registrado". Plantão sem
// `endHour` fica em "Aguardando" — mesmo default documentado de `hasInstantPassed`.
const hasStationEnded = computed(() => hasInstantPassed(stationDetails.value?.endHour))

/**
 * Variante do menu de ações. `none` fora do dashboard (Aprovações) ou sem plantão:
 * os blocos do template já não renderizam nesses casos, e o dock do celular some.
 */
const actionsVariant = computed<StationDetailActionsVariant>(() => {
  if (!stationDetails.value || props.mode !== 'dashboard') return 'none'
  return flags.enabledSmartAllocation ? 'smart' : 'legacy'
})

/**
 * Condições de visibilidade/habilitação/motivo das ações (§9 da spec do mobile). O
 * template desktop lê `actionMap`/`isOverflowAvailable`; o dock do celular lê
 * `stationActions`. Aqui, depois de `hasStationEnded`: as fontes precisam existir.
 */
const {
  actions: stationActions,
  actionMap,
  isOverflowAvailable,
  isSendRecordDisabled,
  sendRecordTooltip
} = useStationDetailActions(
  {
    variant: actionsVariant,
    hasDoctor: () => !!stationDetails.value?.doctorStation?.doctor,
    isUnidentified: isUnidentifiedStation,
    ourTeam: () => !!stationDetails.value?.doctorStation?.ourTeam,
    canAllocateDoctor,
    canEditStation,
    canRemoveDoctor,
    canReadHistory,
    canReadExceptionalities,
    exceptionalitiesEnabled: () => isEnabledException(),
    stationHistoryEnabled: () => isEnabledStationHistory(),
    isPostStationLocked,
    // Trava por perna: lida do registro que este modal já carrega (sem rota nova).
    isLegLocked: () => stationLegLock.value.locked,
    legLockReason: () => stationLegLock.value.reason,
    canEmitActivityRecord,
    scheduleAcceptsActivityRecords,
    hasStationEnded,
    isExceptionalityOpen: isOpenExceptionalitySidebar,
    isHistoryOpen: isOpenHistorySidebar,
    isCheckAttemptsOpen: showCheckAttempts
  },
  // Na variante legada o lápis e o histórico passam por `toggleSidebar` (fecham as
  // outras sidebars); na smart, pelo speed dial — exatamente como os botões de antes.
  // Handler só existe com a variante `smart`/`legacy` (em `none` a lista é vazia).
  {
    allocate: () => openManagement(null),
    edit: () =>
      actionsVariant.value === 'legacy' ? toggleSidebar('edit') : handleSpeedDialAction('edit'),
    sendRecord: () => handleSpeedDialAction('send-record'),
    exceptionality: () => toggleSidebar('exceptionality'),
    checkAttempts: () => toggleCheckAttempts(),
    history: () =>
      actionsVariant.value === 'legacy'
        ? toggleSidebar('history')
        : handleSpeedDialAction('history'),
    remove: () => handleSpeedDialAction('remove')
  }
)

/** Tela cheia só no dashboard: em Aprovações o detalhe divide a tela com a sidebar externa. */
const isFullScreen = computed(() => props.mobileShell && props.mode === 'dashboard')

/**
 * Detalhe pronto para o cabeçalho e o dock do celular. `fetchStationDetails` não zera
 * `stationDetails` ao recarregar (alocação, remoção, registro enviado, volta do Editar)
 * nem no erro: sem este gate, meta, selos e dock mostrariam — e agiriam sobre — o
 * plantão anterior enquanto o corpo está no skeleton ou na mensagem de erro.
 * Só o celular lê: o menu do desktop segue `actionsVariant`.
 */
const isDetailReady = computed(
  () => !loading.value && !errorMessage.value && !!stationDetails.value
)

/** Meta do cabeçalho em tela cheia: "#id · dia · início–fim · escala · unidade". */
const mobileMeta = computed(() => {
  const sd = stationDetails.value
  if (!sd || !isDetailReady.value) return undefined
  const start = formattedStartDate.value
  const end = formattedEndDate.value
  return [
    `#${sd.id}`,
    start ? `${start.weekday}, ${start.date}` : null,
    start && end ? `${start.time}–${end.time}` : null,
    sd.schedule?.name,
    sd.schedule?.healthUnit?.name
  ]
    .filter(Boolean)
    .join(' · ')
})

interface MobileBadge {
  id: 'type' | 'sector' | 'specialty' | 'external'
  label: string
  class: string
}

/** Selos do cabeçalho em tela cheia. "Externo" só quando há médico e ele é de fora. */
const mobileBadges = computed<MobileBadge[]>(() => {
  const sd = stationDetails.value
  if (!sd || !isDetailReady.value) return []
  const badges: MobileBadge[] = [
    { id: 'type', label: formattedStationType.value, class: stationTypeClass.value }
  ]
  if (sd.sector?.name)
    badges.push({ id: 'sector', label: sd.sector.name, class: 'station-badge-neutral' })
  if (sd.specialty?.name) {
    badges.push({ id: 'specialty', label: sd.specialty.name, class: 'station-badge-neutral' })
  }
  if (sd.doctorStation?.doctor && sd.doctorStation.ourTeam === false) {
    badges.push({ id: 'external', label: 'Externo', class: 'station-badge-external' })
  }
  return badges
})

const showDoctorCheck = computed(() =>
  Boolean(
    stationDetails.value?.doctorStation?.doctorChecks &&
    stationDetails.value?.doctorStation?.doctorChecks.length > 0
  )
)

const checkin = computed(() => {
  const raw = mainCheck.value?.checkin
  return raw ? formatDate(raw, stationDetails?.value?.timezone) : null
})

const checkout = computed(() => {
  const raw = mainCheck.value?.checkout
  return raw ? formatDate(raw, stationDetails?.value?.timezone) : null
})

const formattedObservations = computed(() =>
  observations.value.data.map((obs) => {
    // Sem `createdAt` a data simplesmente não existe — "Data inválida" acusava um
    // erro de dado que não aconteceu. O texto de erro fica reservado ao catch,
    // onde a data existe mas não pôde ser formatada.
    let correctedTimestamp = 'Data não disponível'

    if (obs.createdAt) {
      try {
        const utcDate = new Date(obs.createdAt)
        const timezone = stationDetails.value?.timezone || '-03:00'
        const offsetHours = parseInt(timezone.split(':')[0], 10)
        const localDate = addHours(utcDate, offsetHours)

        correctedTimestamp = format(localDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
      } catch (e) {
        console.error('Erro ao formatar o timestamp da observação:', e)
        correctedTimestamp = 'Data inválida'
      }
    }

    return {
      id: obs.id,
      author: obs.author.name,
      authorId: obs.author.id,
      timestamp: correctedTimestamp,
      text: obs.text
    }
  })
)

const baseHourlyRate = computed(() => {
  if (
    exceptionSummary.value?.changes.hourlyValue.requested &&
    exceptionSummary.value?.financials.exceptionalDurationHours
  ) {
    const cost = Number(exceptionSummary.value.changes.hourlyValue.requested)
    const duration = Number(exceptionSummary.value.financials.exceptionalDurationHours)
    if (duration > 0) {
      return cost / duration
    }
    return cost
  }

  if (
    !stationDetails.value ||
    !stationDetails.value.value ||
    !stationDetails.value.startHour ||
    !stationDetails.value.endHour
  ) {
    return 0
  }

  const start = new Date(stationDetails.value.startHour)
  const end = new Date(stationDetails.value.endHour)

  const durationInHours = differenceInHours(end, start)

  if (durationInHours <= 0) {
    return stationDetails.value.value
  }

  return stationDetails.value.value / durationInHours
})

async function fetchExceptionDetails(
  doctorStationId: number | undefined,
  exceptionStatus: string | null | undefined,
  exceptionRequestId: any
) {
  if (!doctorStationId || !exceptionStatus || !exceptionRequestId) {
    exceptionSummary.value = null
    return
  }

  try {
    exceptionSummary.value = await exceptionService.getDetails(exceptionRequestId)
  } catch (exceptionErr: any) {
    console.warn('Erro ao carregar detalhes da exceção:', exceptionErr)
    exceptionSummary.value = null
  }
}

async function fetchObservationsAndAttendance(
  doctorStationId: number | undefined,
  startHour: string | undefined
) {
  if (!doctorStationId) {
    observations.value = { data: [], count: 0 }
    return
  }

  observations.value = await NetworkService.observation.list(doctorStationId)

  if (!flags.enabledDsAttendancePolicy) return

  const stationDate = startHour ? new Date(startHour).toISOString().slice(0, 10) : null
  if (stationDate) {
    // Isolado do restante do carregamento de propósito: a checagem de presença é
    // secundária e não pode derrubar o modal inteiro. O erro fica visível na seção
    // (`attendanceStatusError`) em vez de o service devolver "sem registro aprovado",
    // que destravaria a edição do plantão por falha de rede.
    attendanceStatusError.value = null
    try {
      attendanceStatus.value = await NetworkService.dsAttendance.getStatusByStation(
        doctorStationId,
        stationDate
      )
    } catch (error) {
      console.error('Erro ao verificar o registro de presença do plantão:', error)
      attendanceStatus.value = { hasApprovedRecord: false }
      attendanceStatusError.value = getApiErrorMessage(
        error,
        'Não foi possível verificar o registro de presença deste plantão.'
      )
    }
  }
}

/**
 * Isolada do restante do carregamento pelo mesmo motivo da checagem de presença:
 * uma falha aqui vira estado DENTRO da seção (balão ⑥), nunca um modal vazio.
 */
/**
 * O registro agora também gateia ações (trava por perna). Dois cuidados:
 * - resposta atrasada de um plantão anterior é descartada (`registrationRequestSeq`);
 * - ao trocar de doctorStation o registro anterior é zerado na hora, para a trava
 *   dele nunca valer para o novo. No recarregamento do MESMO doctorStation (retry,
 *   envio de registro concluído) o atual fica até a resposta, sem piscar a seção.
 */
let registrationRequestSeq = 0
let registrationDoctorStationId: number | null = null

async function fetchStationRegistration(doctorStationId?: number) {
  const seq = ++registrationRequestSeq
  const id = doctorStationId ?? stationDetails.value?.doctorStation?.id
  if (!id || !canReadDoctorCheck.value) {
    stationRegistration.value = null
    registrationDoctorStationId = null
    return
  }

  if (registrationDoctorStationId !== Number(id)) {
    stationRegistration.value = null
    registrationDoctorStationId = Number(id)
  }
  registrationLoadError.value = null
  try {
    const registration = await NetworkService.station.getStationRegistration(Number(id))
    if (seq !== registrationRequestSeq) return
    stationRegistration.value = registration
  } catch (error) {
    if (seq !== registrationRequestSeq) return
    console.error('Erro ao carregar o registro do plantão:', error)
    stationRegistration.value = null
    registrationLoadError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar o registro deste plantão.'
    )
  }
}

/* --------------------------------------------------------------------------- *
 * F4 — "Ver evidências": o modal com uma aba por perna
 * --------------------------------------------------------------------------- */

const isStationEvidenceModalOpen = ref(false)
const stationEvidenceContext = ref<StationEvidenceContext | null>(null)

/**
 * O contexto chega PRONTO da seção — só ela sabe qual fonte venceu cada perna e
 * onde essa fonte mora. O roteamento por tipo de fonte (folha, app, Registro de
 * Atividade) mora dentro do modal, junto dos painéis que o exibem.
 */
function openStationEvidence(context: StationEvidenceContext) {
  stationEvidenceContext.value = context
  isStationEvidenceModalOpen.value = true
}

// Trocar de plantão com o modal aberto mostraria a evidência do plantão anterior.
watch(
  () => [props.infos?.stationId, stationRegistration.value],
  () => {
    isStationEvidenceModalOpen.value = false
    stationEvidenceContext.value = null
  }
)

/* --------------------------------------------------------------------------- *
 * M2 — "Enviar registro" (SendRecordModal da W4)
 * --------------------------------------------------------------------------- */

const isSendRecordModalOpen = ref(false)
const activityRecordsSectionRef = ref<{ fetchRecords: () => Promise<void> } | null>(null)

/** Wizard de folha aberto pelo passo 1 do SendRecordModal, com o handoff da vaga (C6). */
const isAttendanceUploadModalOpen = ref(false)
const sheetInitialForm = ref<Partial<DsAttendanceUploadForm> | null>(null)
const sheetLockedFields = ref<string[]>([])

function openSendRecordModal() {
  if (isSendRecordDisabled.value) return
  isSendRecordModalOpen.value = true
}

/**
 * Perna que o registro da vaga ainda não tem. `null` quando as duas já têm fonte
 * (ou quando o backend de registro ainda não responde) — o modal decide sozinho
 * nesse caso.
 */
const missingRegistrationLegs = computed<CoveredLegs | null>(() => {
  const registration = stationRegistration.value
  if (!registration) return null

  const hasCheckin = !!registration.checkin?.value
  const hasCheckout = !!registration.checkout?.value
  if (!hasCheckin && !hasCheckout) return CoveredLegs.BOTH
  if (!hasCheckin) return CoveredLegs.CHECKIN
  if (!hasCheckout) return CoveredLegs.CHECKOUT
  return null
})

/**
 * Contexto da vaga de origem (§2 de M2). `null` enquanto o detalhe não carregou
 * ou a vaga não tem alocação: sem `doctorStationId` não há registro a enviar, e
 * o modal nem é montado.
 *
 * "Adicionar outros plantões" (passo 2) não depende deste contexto (BF16): o
 * próprio `SendRecordModal` busca os candidatos do médico via
 * `getActivityRecordCandidates` a partir do `doctorStationId` — o Detalhes do
 * Plantão só conhece a vaga de origem, e nunca populou esse campo.
 */
function buildSendRecordStation(sd: ShiftDetails, doctorStationId: number) {
  return {
    doctorStationId,
    referenceDate: sd.startHour ?? null,
    expectedStart: sd.startHour ?? null,
    expectedEnd: sd.endHour ?? null,
    doctorName: sd.doctorStation?.doctor?.mainCrm?.name ?? null,
    scheduleName: sd.schedule?.name ?? null,
    healthUnitName: sd.schedule?.healthUnit?.name ?? null,
    shiftName: sd.shift?.name ?? null,
    shiftId: sd.shift?.id ?? null,
    registrationStatus: stationRegistration.value?.status ?? null,
    missingLegs: missingRegistrationLegs.value
  }
}

const sendRecordContext = computed<SendRecordContext | null>(() => {
  const sd = stationDetails.value
  const doctorStationId = Number(sd?.doctorStation?.id ?? 0)
  if (!sd || !doctorStationId) return null

  const settings = dsAttendanceStore.registrationSettings
  const healthUnit = sd.schedule?.healthUnit

  return {
    doctorStationId,
    scheduleId: Number(sd.schedule?.id ?? 0),
    companyId: healthUnit?.company?.id ?? null,
    healthUnitId: healthUnit?.id ?? null,
    referenceDate: sd.startHour ?? null,
    station: buildSendRecordStation(sd, doctorStationId),
    acceptedActivityTypes: settings.acceptedActivityTypes,
    checkinLateToleranceMinutes: settings.checkinLateToleranceMinutes,
    checkoutEarlyToleranceMinutes: settings.checkoutEarlyToleranceMinutes,
    // A config aqui é buscada COM o `scheduleId` da vaga, então a modalidade que
    // viaja é a da política desta escala — é ela que decide se o passo 1 ainda
    // pode oferecer "Registro de Plantão".
    policyMonitoringType: settings.monitoringType ?? null,
    hasSheetRuleInEffect: settings.hasSheetRuleInEffect ?? null
  }
})

/**
 * Envio concluído: as duas seções de registro desta tela ficam desatualizadas na
 * hora. Recarrega o registro por perna (W2) e a lista de Registros de Atividade
 * da vaga, sem refazer o `GET /stations/:id` inteiro.
 */
async function handleSendRecordSaved() {
  await Promise.all([
    fetchStationRegistration(),
    activityRecordsSectionRef.value?.fetchRecords() ?? Promise.resolve()
  ])
}

/**
 * "Registro de Plantão" no passo 1 (C6). O wizard de folha abre AQUI mesmo, com o
 * `buildSheetHandoff` traduzindo o payload da vaga em `initialForm`/`lockedFields`.
 *
 * Antes esta função descartava o payload e abria a fila em nova aba: o usuário caía num
 * formulário em branco e tinha de redigitar empresa, unidade, data e escala que a tela já
 * conhecia — e uma divergência ali gera folha para o plantão errado. Abrir localmente
 * também preserva o que estiver aberto no Dashboard, que era a razão da nova aba.
 */
function handleRedirectToSheet(payload: RedirectToSheetPayload) {
  const handoff = buildSheetHandoff(payload)
  sheetInitialForm.value = handoff.form
  sheetLockedFields.value = handoff.lockedFields
  isSendRecordModalOpen.value = false
  isAttendanceUploadModalOpen.value = true
}

async function fetchStationDetails() {
  const stationId = props.infos?.stationId
  if (!stationId) {
    stationDetails.value = null
    exceptionSummary.value = null
    stationRegistration.value = null
    registrationLoadError.value = null
    // Invalida um registro ainda em voo do plantão anterior.
    registrationRequestSeq++
    registrationDoctorStationId = null
    return
  }

  loading.value = true
  errorMessage.value = null

  try {
    const res = await NetworkService.station.getStationDetails(stationId)

    stationDetails.value = res
    lastFetchedStationId.value = stationId

    const doctorStationId = res?.doctorStation?.id
    const exceptionStatus = res?.doctorStation?.exceptionSummaryStatus
    const exceptionRequestId = props.exceptionIdSelected ?? res?.doctorStation?.approvedExceptionId

    await fetchExceptionDetails(doctorStationId, exceptionStatus, exceptionRequestId)
    // Em paralelo: a sondagem de pós-plantão não depende de observações nem de
    // presença, e precisa estar resolvida antes de `loading` virar false — ela
    // alimenta o `PostStationDetailsSection` e a trava de edição; decidir depois
    // faria o painel de pós-plantão trocar de estado na cara do usuário.
    //
    // Depois de `stationDetails.value = res` de propósito: a `scheduleId` que
    // gateia a leitura sai do detalhe do plantão, então sondar antes não passaria
    // do gate de permissão.
    await Promise.all([
      fetchObservationsAndAttendance(doctorStationId, res?.startHour),
      refreshPostStationLock(),
      fetchStationRegistration(doctorStationId),
      // Config de registro da ESCALA deste plantão: decide se "Enviar registro"
      // fica habilitado. A ação nunca lança (default seguro no store).
      dsAttendanceStore.fetchRegistrationSettings(res?.schedule?.id)
    ])
  } catch (err: any) {
    // Estado de erro visível no corpo do modal — com a mensagem específica do
    // ServiceError (permissão, rede, timeout...) em vez do texto fixo.
    errorMessage.value = getApiErrorMessage(
      err,
      'Não foi possível carregar os detalhes do plantão.'
    )
    console.error(err)
  } finally {
    loading.value = false

    // Deep-link (#1550): no Dashboard o modal é o dono das sidebars e abre a
    // `ExceptionalitiesSidebar` sozinho. Nas Aprovações (`mode === 'approvals'`)
    // a página já abre a própria Request/Summary por fora (`isPushedExternally`);
    // abrir também a interna empilhava uma sidebar duplicada, escondida atrás da
    // externa, com duas chamadas de API redundantes — e era ela que "sobrava"
    // na tela após aprovar/recusar.
    if (
      props.mode !== 'approvals' &&
      props.exceptionIdSelected &&
      stationDetails.value?.doctorStation
    ) {
      nextTick(() => {
        // Reconfere no callback: se o consumidor escondeu o modal enquanto o
        // fetch estava em voo, `closeAllSidebars()` já rodou e este tick
        // pendente não pode reabrir a sidebar por cima de um modal invisível.
        if (props.showModal) isOpenExceptionalitySidebar.value = true
      })
    }
  }
}

watch(
  () => props.showModal,
  (isModalVisible) => {
    if (isModalVisible) {
      if (props.infos?.stationId) {
        fetchStationDetails()
      } else {
        stationDetails.value = null
        loading.value = false
      }
      return
    }
    // Modal escondido por prop pelo consumidor (sem passar pelo `close()`
    // interno) — é o que a página de Aprovações faz ao aprovar/recusar. Sem este
    // reset, a `ExceptionalitiesSidebar` aberta pelo `exceptionIdSelected` (e a
    // Summary dentro dela) ficava visível sobre a página com o modal invisível.
    closeAllSidebars()
  },
  // immediate: true para tratar mudanças de showModal que chegam enquanto fetch
  // está em voo, evitando reabrir sidebars sobre um modal invisível
  { immediate: true }
)

// Cobre só a troca "por baixo" do stationId com o modal já aberto (ex.: clique
// na linha de vínculo de Registro de Permanência, que atualiza `stationSelected`
// no consumidor sem fechar/reabrir este modal) — a abertura inicial já é
// tratada pelo watch de `props.showModal` acima.
watch(
  () => props.infos?.stationId,
  (newStationId, oldStationId) => {
    if (shouldRefetchOnStationIdChange(props.showModal, newStationId, oldStationId)) {
      fetchStationDetails()
    }
  }
)

function revokeEvidenceBlobUrl() {
  if (evidenceBlobUrl.value?.startsWith('blob:')) {
    URL.revokeObjectURL(evidenceBlobUrl.value)
  }
  evidenceBlobUrl.value = undefined
  // Revoga blob URLs criadas em `openEvidence` para evidências de check
  // (cada arquivo é baixado via endpoint streaming e ganha sua própria
  // blob URL no `path`).
  selectedEvidenceFiles.value.forEach((file) => {
    if (file.path?.startsWith('blob:')) {
      URL.revokeObjectURL(file.path)
    }
  })
}

async function openEvidence(files: CheckEvidence[]) {
  if (!files.length) return
  const stationId = props.infos?.stationId
  if (!stationId) return
  revokeEvidenceBlobUrl()
  loadingDsEvidence.value = true
  let failedCount = 0
  try {
    // O `path` em `CheckEvidence` agora é um token cifrado pelo backend;
    // baixamos cada evidência via endpoint streaming autenticado e
    // substituímos o `path` por uma blob URL local — assim o
    // `EvidenceModal`/`StepPreview` continuam recebendo um URL plotável.
    const downloadable = await Promise.all(
      files.map(async (file) => {
        if (!file.id) return file
        try {
          const blob = await NetworkService.doctorCheckEvidence.downloadCheckEvidenceFile(
            stationId,
            file.id
          )
          return { ...file, path: URL.createObjectURL(blob), type: blob.type }
        } catch (err) {
          console.error(`Falha ao baixar evidência ${file.id}:`, err)
          failedCount += 1
          // Substitui o token cifrado por string vazia para impedir que o
          // `StepPreview` tente abrir o token como URL e mostre lixo.
          return { ...file, path: '' }
        }
      })
    )
    if (failedCount === files.length) {
      toast.error('Não foi possível carregar a evidência. Tente novamente em instantes.')
      return
    }
    if (failedCount > 0) {
      toast.warning(`Não foi possível carregar ${failedCount} arquivo(s) de evidência.`)
    }
    // A coluna "Evidência" agora só trata anexos: a heurística que desviava
    // checks do app para o modal de biometria saiu junto com o
    // `BiometryEvidenceModal` — a biometria tem coluna e modal próprios.
    selectedEvidence.value = downloadable[0]
    selectedEvidenceFiles.value = downloadable
    historyEvents.value = []
    evidenceUploader.value = downloadable[0].uploadedBy?.name ?? ''
    isEvidenceModalOpen.value = true
    const containerId = attendanceStatus.value.containerId
    if (containerId) fetchHistoryEvents(containerId)
  } finally {
    loadingDsEvidence.value = false
  }
}

async function openDsAttendanceEvidence(containerIdOverride: number | null = null) {
  const containerId = containerIdOverride ?? attendanceStatus.value.containerId
  if (!containerId) return
  revokeEvidenceBlobUrl()
  loadingDsEvidence.value = true
  try {
    const [blob, detail] = await Promise.all([
      NetworkService.dsAttendance.downloadEvidenceFile(containerId),
      NetworkService.dsAttendance.getById(containerId).catch(() => null)
    ])
    const blobUrl = URL.createObjectURL(blob)
    evidenceBlobUrl.value = blobUrl
    const evidence: CheckEvidence = {
      name: detail?.sheetFilename || 'Folha de monitoramento.pdf',
      path: blobUrl,
      type: blob.type
    }
    selectedEvidence.value = evidence
    selectedEvidenceFiles.value = [evidence]
    evidenceUploader.value = ''
    isEvidenceModalOpen.value = true
    fetchHistoryEvents(containerId)
  } catch (e) {
    // Antes o clique simplesmente não fazia nada: a falha morria no console. Endpoint
    // `blob` — com o unwrap no service, o motivo real chega ao usuário.
    console.error('Erro ao buscar evidência do registro de presença:', e)
    notifyError(e, 'Não foi possível carregar a evidência do registro de presença.')
  } finally {
    loadingDsEvidence.value = false
  }
}
</script>

<style scoped>
.station-semanal {
  background-color: #d8eef3;
  border: 1px solid #2f7f91;
}
.station-cobertura {
  background-color: #fce9ea;
  border: 1px solid #da1e28;
}
.station-extra {
  background-color: #eafbef;
  border: 1px solid #198038;
}
.station-aberta {
  background-color: var(--st-open-bg, #fdf2e9);
  border: 1px solid var(--st-open-stripe, #f18f1b);
}
.station-badge-neutral {
  background-color: #f2f4f8;
  border: 1px solid #c1c7cd;
}
.station-badge-external {
  background-color: #fff8e1;
  border: 1px solid #f1c21b;
}

.modal-pushed-by-sidebar {
  transform: translateX(-14rem);
  transition: transform 300ms ease;
}

@media screen and (max-width: 767px) {
  .modal-pushed-by-sidebar {
    transform: none;
  }
}
</style>
